import { createContext, useContext, useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    Input, Textarea, Spinner, Tooltip,
    Autocomplete, AutocompleteItem, Popover, PopoverTrigger, PopoverContent, Calendar,
    Switch, Tabs, Tab,
} from '@nextui-org/react';
import { parseDate } from '@internationalized/date';
import { I18nProvider } from '@react-aria/i18n';
import { CtyButton } from '../button/CtyButton';
import { CtyShowToast, checkType } from '../toast/CtyToast';
import { formatFileSize, formatKeyForDisplay, getFileType } from '../utils/commonutils';
import {
    buildActiveWebAgentConfigQuery,
    loadWebAgentSpecByCode,
    parseInstructionsGroups,
    runWebAgent,
    useExecuteCommandIntegrationMutation,
    useExecuteCommandRequestMutation,
    useGetAiworkersPresignedUrlMutation,
    useLazyGetAiWorkerConfigurationsQuery,
    useLazyGetCommandSourceRecordsQuery,
    useUploadFileToS3Mutation,
    type WebAgentStep,
} from '@circuitry-ai/doc-data';
import { Icon } from '@iconify/react';
import { HtmlTemplateFrame } from '../template/UxgenieTemplateContentView';
import { useSorApi, type SorFilter, type SorQueryResponse } from '../hooks/useSorApi';
import { isSorEnabled } from '../sor/sorTokenStore';
import { InspectionChecklistEditor, type InspectionChecklistItem } from '../inspection-checklist/InspectionChecklistEditor';
import {
    getMediaType,
    isInspectionMediaType,
    MEDIA_TYPE_TO_EVIDENCE_KIND,
} from '../inspection-checklist/inspectionChecklistMedia';
import { AttachmentPreviewSection } from '../promptinput/attachmentUploadShared';
import { UploadedFileType } from '../utils/constants';


/**
 * Where a `dropdown`/`radio`/`list` field's option data comes from. Lets a single agent's
 * command json declare an arbitrary data source instead of the renderer hardcoding one.
 *  - `static`:      options come from `FieldDef.options` in the json itself.
 *  - `localStorage`: options/records come from `JSON.parse(localStorage.getItem(key))`.
 *  - `api`:          an `endpoint` starting with `/` is fetched directly (e.g.
 *                     `/api/v1/aiworkers?category=uxgenie&sub_type=inspection`), reading
 *                     `valueField`/`labelField` off each returned record; any other `endpoint` is
 *                     looked up against a host-registered resolver (see
 *                     `setCommandDataSourceResolver`). Back-compat: `sourceDropdown:
 *                     'configuration-actions'` still works and is treated as `{kind:'api', endpoint:
 *                     'configuration-actions'}`.
 *  - `ref`:          (for `list` fields only) reuses the record currently selected on another field
 *                     (`field`) and reads `path` off it (e.g. the selected Inspection Template's
 *                     `items` array) rather than fetching anything itself.
 *  - `sor`:          records of the System of Record `definition`, read with
 *                     `POST /api/v1/records/query` (all pages). `filter` is the query's filter tree;
 *                     on an `inspectionItems` field its `{{form.<field>}}` placeholders are filled
 *                     from the form and the query reruns when they change.
 *  - `valueField` / `labelField`: a key on each record, or a dot path such as `data.category`.
 *  - `pick`:         reads one string from an array on each record (`list` + `where` + `value`).
 *  - `distinct`:     one option per unique picked or `valueField` value. Blank values are omitted.
 *  - `when`:         keeps a record only when its `pick` (or the record's `valueField` path) equals
 *                     another field's current value.
 */
export type SourcePick = {
    list: string;
    where: Record<string, string>;
    value: string;
};

export type FieldSource = {
    kind: 'static' | 'localStorage' | 'api' | 'ref' | 'sor';
    key?: string;
    endpoint?: string;
    /** `sor` only: definition key, e.g. `slm.insp.inspection_template`. */
    definition?: string;
    /** `sor` only: the records query filter. */
    filter?: SorFilter | null;
    field?: string;
    path?: string;
    valueField?: string;
    labelField?: string;
    pick?: SourcePick;
    distinct?: boolean;
    when?: { field: string; pick?: SourcePick; valueField?: string };
};

export type CommandFieldOption = { value: string; label: string };

/** One choice offered when adding a new item to a `list` field (e.g. "Photo" vs "Video"). */
export type ListAddOption = { value: string; label: string; icon?: string };

/** How to render each record when a `list` field's items are objects (not plain strings), and
 *  whether/how the user can add new ad-hoc items on top of the source-provided ones. */
export type ListItemSchema = {
    labelField?: string;
    /** Field on a source item holding its initial enabled/toggled state (boolean). When a source
     *  item doesn't have this field (or it isn't set), a new item defaults to enabled. */
    toggleField?: string;
    requiredField?: string;
    /** When true, a required item's toggle is locked on (can't be disabled). Default false — a
     *  required item is still just a visual "*" marker and stays user-toggleable unless a JSON
     *  author opts into locking it. */
    lockRequired?: boolean;
    /** Field on an item that already holds a ready-to-use icon name — takes precedence over the
     *  `typeField`/`addOptions` icon lookup below. */
    iconField?: string;
    /** Field on a newly-added item that stores which `addOptions` choice was picked (default `mediaType`). */
    typeField?: string;
    /** Icon used when neither `iconField` nor a matching `addOptions[].icon` resolves one. When
     *  omitted, no icon is rendered for that item. */
    defaultIcon?: string;
    /** Lets the user append new checklist items beyond the source-provided list. Default false. */
    allowAdd?: boolean;
    /** Text for the "+ Add" trigger. Defaults to a generic "Add an item". */
    addLabel?: string;
    /** Icon for the "+ Add" trigger. Defaults to a generic "add" icon. */
    addIcon?: string;
    /** When present, adding an item first asks the user to pick one of these (e.g. Photo/Video)
     *  before the new row appears; when omitted, a new blank row is added immediately. */
    addOptions?: ListAddOption[];
    /** Prompt shown above the `addOptions` buttons. */
    addPrompt?: string;
    /** Placeholder for a newly-added item's editable name input. Defaults to a generic "Item name". */
    itemPlaceholder?: string;
    /** Label shown for an item whose name is still blank. Defaults to a generic "Untitled item". */
    emptyItemLabel?: string;
    /** Accessible label for a user-added item's remove button. Defaults to a generic "Remove item". */
    removeLabel?: string;
    /** Icon for a user-added item's remove button. Defaults to a generic "remove" icon. */
    removeIcon?: string;
    /** Message shown when the resolved item list is empty. Defaults to a generic "No items configured". */
    emptyStateLabel?: string;
};

/** One field inside a `repeatable` item. `required` defaults to true, same as a top-level field. */
export type RepeatableField = {
    type?: 'string' | 'number' | 'date' | 'dropdown' | 'attachment';
    label?: string;
    multiline?: boolean;
    required?: boolean;
    options?: CommandFieldOption[];
};

/** A tab's own content — a self-contained field definition scoped to that tab, so each tab can
 *  have a different `source`/`itemSchema` (a `list` tab) or hold free text under its own key
 *  (a plain-text tab). Falls back to the parent `tabs` field's own `source`/`itemSchema` when a
 *  `list` tab omits `content` (the older, single-list-per-tabs-field shape). */
export type TabContent = {
    type?: 'list' | 'string';
    source?: FieldSource;
    itemSchema?: ListItemSchema;
    multiline?: boolean;
    /** formData key the tab's free text is stored under. Defaults to `${tabsFieldKey}__${tabKey}`. */
    field?: string;
};

/** One tab inside a `tabs` field. `kind`/content type controls whether it renders a checklist or
 *  a free-text box; when `kind` is omitted it's inferred from the tab's key/label or `content.type`. */
export type TabDef = { key: string; label: string; kind?: 'list' | 'message'; content?: TabContent };

export type CommandRequestStep = {
    /** Name under which the extracted response is exposed to later `{{step.path}}` placeholders. */
    key: string;
    /** `request` (default) calls `endpoint`; `webAgent` runs the web agent whose code is `agentCode`;
     *  `integration` runs the configured integration action whose code is `code`, server side, with
     *  `payload` — for services the browser can't call directly (e.g. no CORS). */
    kind?: 'request' | 'webAgent' | 'integration';
    /** `/…` calls this application's API; `https://…` an external service, sent only `headers`.
     *  For `integration`, an optional `/…` path overriding the default integrations endpoint. */
    endpoint?: string;
    method?: string;
    body?: unknown;
    headers?: Record<string, string>;
    agentCode?: string;
    /** Integration action code, for `kind: 'integration'`. */
    code?: string;
    /** Payload the integration action receives (headers, `path_parameters`, …), for `kind: 'integration'`. */
    payload?: unknown;
    /** Run inputs the web agent's steps reference as `${name}`. */
    inputs?: Record<string, unknown>;
    /** Dot path selected from the response. Omit to retain the full response. */
    responsePath?: string;
    /** Several paths selected from the response, each kept under its alias. Wins over `responsePath`. */
    select?: Record<string, string>;
};

/**
 * Fills a field from a named request chain declared under the command json's `$sources` key
 * (`{ "<name>": { "dependsOn": [...], "steps": [...] } }`), so several fields share one run.
 * `path` reads the chain's step results; `match` (`email` | `phone`) or a custom regex `pattern`
 * keeps the first match found in that value's text.
 */
export type FieldPrefill = { source: string; path: string; match?: string; pattern?: string };

export type CommandIframeConfig = {
    /** Form fields that must be non-empty before the request chain runs. */
    dependsOn?: string[];
    /** Requests run in order; each result is available to subsequent steps by its `key`. */
    steps: CommandRequestStep[];
    /** Template resolving to the iframe URL after all steps finish, e.g. `{{presignedUrl}}`. */
    url: string;
    readyEvent?: string;
    /** Data posted to the iframe when it sends `readyEvent`. */
    readyPayload?: unknown;
    /** When set, only iframe messages with this `type` are treated as output. */
    outputEvent?: string;
    /** Form-data key that receives the output as JSON. Defaults to the field's own key. */
    outputField?: string;
    /** Dot path selected from the iframe message before storing it. Omit for the full message. */
    outputPath?: string;
    height?: number;
};

export type FieldDef = {
    key: string;
    type: 'string' | 'number' | 'dropdown' | 'hidden' | 'date' | 'radio' | 'list' | 'tabs' | 'iframe' | 'repeatable'
        | 'inspectionItems' | 'attachment';
    /** Display label. Falls back to a title-cased version of `key` when the json omits it. */
    label?: string;
    /** @deprecated Ignored for layout. Fields flow from the form width; set `width` to size one. */
    row?: number;
    /** Share of the row this field takes. `"100%"` / `"full"`, `"50%"` / `"half"`, `"33%"` /
     *  `"third"`, or a column count from 1 to 12. Omitted plain fields share the row (one on a
     *  narrow form, two from 500px, three from 1064px). Omitted `list` / `tabs` / `iframe` fields
     *  take the whole row. A narrow form always stacks fields to full width. */
    width?: string | number;
    /** Renders a `string` field as a multi-line textarea instead of a single-line input. */
    multiline?: boolean;
    /** @deprecated use `source: {kind:'api', endpoint}` instead. Kept for existing agent configs. */
    sourceDropdown?: string;
    /** True for the legacy configuration-actions picker. Derived once so callers never read `sourceDropdown`. */
    legacyActionDropdown?: boolean;
    source?: FieldSource;
    options?: CommandFieldOption[];
    /** Show/require this field only when another field currently satisfies a condition: an
     *  exact `equals` match, a match against any value in `in` (e.g. Phone Number for both
     *  `sms` and `email_sms`), or (for a field like a dropdown whose value can be anything, e.g.
     *  "show the checklist once any Inspection Template is selected") `notEmpty: true`. */
    visibleWhen?: { field: string; equals?: string; in?: string[]; notEmpty?: boolean };
    itemSchema?: ListItemSchema;
    tabs?: TabDef[];
    /** Host-page element selector used by a registered prefill resolver (PAL extension). */
    inputElementRef?: string;
    /** When false, field is optional (no asterisk / validation). Defaults to true. Ignored for `hidden`. */
    required?: boolean;
    /** Value the field starts with (json `default`) when the form has no value for it yet. Not
     *  supported for `list`/`tabs`, whose state is a composite json blob rather than a scalar. */
    defaultValue?: string;
    iframe?: CommandIframeConfig;
    prefill?: FieldPrefill;
    /** `repeatable` only: the fields on each item the user adds. Stored as a JSON array of objects. */
    fields?: Record<string, RepeatableField>;
};

/** A field that is rendered in the UI: not `hidden`, its `visibleWhen` (if any) is satisfied, and
 *  — for a `list`/`tabs` field sourced via `ref` — the field it depends on already has a value
 *  (e.g. the checklist only appears once an Inspection Template is selected). This holds even
 *  without an explicit `visibleWhen`, since a `ref`-sourced field has nothing to show otherwise. */
const hasFormValue = (formData: Record<string, string>, key?: string): boolean =>
    !key || Boolean((formData[key] ?? '').trim());

const matchesVisibleWhen = (fd: FieldDef, formData: Record<string, string>): boolean => {
    if (!fd.visibleWhen) return true;
    const value = formData[fd.visibleWhen.field] ?? '';
    if (fd.visibleWhen.notEmpty) return Boolean(value.trim());
    if (fd.visibleWhen.in) return fd.visibleWhen.in.includes(value);
    return value === fd.visibleWhen.equals;
};

const referencedFieldKeys = (fd: FieldDef): Array<string | undefined> => {
    const sourceKey = fd.source?.kind === 'ref' ? fd.source.field : undefined;
    const tabKeys = fd.type === 'tabs'
        ? (fd.tabs ?? []).map((tab) =>
            tab.content?.source?.kind === 'ref' ? tab.content.source.field : undefined)
        : [];
    return [sourceKey, ...tabKeys];
};

const isVisibleField = (fd: FieldDef, formData: Record<string, string>): boolean =>
    fd.type !== 'hidden'
    && matchesVisibleWhen(fd, formData)
    && referencedFieldKeys(fd).every((key) => hasFormValue(formData, key));

/** Visible required fields only. Hidden/not-currently-visible fields are never required in the UI sense. */
const isRequiredField = (fd: FieldDef, formData: Record<string, string>): boolean =>
    isVisibleField(fd, formData) && fd.required !== false;

/** The formData key a field's value is stored under. Normally the field's own key, except for an
 *  `iframe` field configured to report its output under a different name (`iframe.outputField`) —
 *  validation, progress and the payload all have to read the value where the config puts it. */
const fieldValueKey = (fd: FieldDef): string => fd.iframe?.outputField ?? fd.key;

const EXCLUDED_BOT_DATA_KEYS = new Set([
    'configuration_id', 'agent_name', 'agent_params',
    'actions', 'rules',
]);

const CUSTOM_DROPDOWN_KEY = 'custom';
const LEGACY_API_ENDPOINT = 'configuration-actions';
/** Keeps a dropdown value from matching its visible label, which makes NextUI snap back to the previous choice. */
const COMMAND_OPTION_KEY_PREFIX = '\u0001';
const toCommandOptionKey = (value: string) => `${COMMAND_OPTION_KEY_PREFIX}${value}`;
const fromCommandOptionKey = (key: string) => (
    key.startsWith(COMMAND_OPTION_KEY_PREFIX) ? key.slice(COMMAND_OPTION_KEY_PREFIX.length) : key
);

const KNOWN_FIELD_TYPES = new Set<FieldDef['type']>(
    ['string', 'number', 'dropdown', 'hidden', 'date', 'radio', 'list', 'tabs', 'iframe', 'repeatable', 'inspectionItems', 'attachment'],
);

function parsePick(value: unknown): SourcePick | undefined {
    const pick = optionalObject<Record<string, any>>(value);
    const list = typeof pick?.list === 'string' ? pick.list.trim() : '';
    const pickedValue = typeof pick?.value === 'string' ? pick.value.trim() : '';
    const whereRaw = optionalObject<Record<string, unknown>>(pick?.where);
    if (!list || !pickedValue || !whereRaw) return undefined;
    const where: Record<string, string> = {};
    for (const [key, item] of Object.entries(whereRaw)) {
        if (typeof item === 'string') where[key] = item;
    }
    return Object.keys(where).length > 0 ? { list, where, value: pickedValue } : undefined;
}

/** Normalizes the legacy `sourceDropdown` string onto the generic `FieldSource` shape. */
function resolveSource(typeDef: any): FieldSource | undefined {
    if (typeDef.source && typeof typeDef.source === 'object') {
        const source = typeDef.source as FieldSource;
        const pick = parsePick(source.pick);
        const whenField = typeof source.when?.field === 'string' ? source.when.field.trim() : '';
        const whenPick = parsePick(source.when?.pick);
        const whenValueField = typeof source.when?.valueField === 'string' ? source.when.valueField.trim() : '';
        let when: FieldSource['when'];
        if (whenField && whenPick) when = { field: whenField, pick: whenPick };
        else if (whenField && whenValueField) when = { field: whenField, valueField: whenValueField };
        return {
            ...source,
            pick,
            distinct: source.distinct === true,
            when,
        };
    }
    if (typeof typeDef.source_dropdown === 'string' && typeDef.source_dropdown) {
        return { kind: 'api', endpoint: typeDef.source_dropdown };
    }
    return undefined;
}

const DATE_MDY = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/u;
const DATE_YMD = /^(\d{4})-(\d{2})-(\d{2})$/u;

/** Canonical command-form date storage: MM/DD/YYYY. */
function toCommandDateMdy(raw: string): string {
    const t = raw.trim();
    if (!t) return '';
    const mdy = DATE_MDY.exec(t);
    if (mdy) return `${mdy[1].padStart(2, '0')}/${mdy[2].padStart(2, '0')}/${mdy[3]}`;
    const ymd = DATE_YMD.exec(t);
    if (ymd) return `${ymd[2]}/${ymd[3]}/${ymd[1]}`;
    return t;
}

/** CalendarDate-compatible YYYY-MM-DD from stored MM/DD/YYYY (or YMD). */
function toCommandDateYmd(raw: string): string | null {
    const t = raw.trim();
    if (!t) return null;
    const mdy = DATE_MDY.exec(t);
    if (mdy) return `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
    if (DATE_YMD.test(t)) return t;
    return null;
}

/**
 * Auto-insert slashes while typing: MM / DD / YYYY.
 * Digits only (max 8); `/` is added by us, not the user. No digits past YYYY.
 */
function formatDateMask(raw: string): string {
    const digits = String(raw ?? '').replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

const optionalObject = <T,>(value: unknown): T | undefined =>
    value && typeof value === 'object' ? value as T : undefined;

function parseDefaultValue(type: FieldDef['type'], value: unknown): string | undefined {
    if (type === 'list' || type === 'tabs' || type === 'repeatable' || type === 'inspectionItems' || type === 'attachment') return undefined;
    return ['string', 'number'].includes(typeof value) ? String(value) : undefined;
}

function parseFieldWidth(value: unknown): string | number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) return value.trim();
    return undefined;
}

function parseFieldDef([key, def]: [string, any]): FieldDef {
    const typeDef = optionalObject<Record<string, any>>(def) ?? { type: 'string' };
    const type: FieldDef['type'] = KNOWN_FIELD_TYPES.has(typeDef.type) ? typeDef.type : 'string';
    const label = typeof typeDef.label === 'string' ? typeDef.label.trim() : '';
    const visibleWhen = optionalObject<FieldDef['visibleWhen']>(typeDef.visibleWhen);
    const source = resolveSource(typeDef);
    return {
        key,
        type,
        label: label || undefined,
        row: typeof typeDef.row === 'number' ? typeDef.row : undefined,
        width: parseFieldWidth(typeDef.width),
        multiline: typeDef.multiline === true,
        sourceDropdown: typeDef.source_dropdown,
        source,
        legacyActionDropdown: typeDef.source_dropdown === LEGACY_API_ENDPOINT || source?.endpoint === LEGACY_API_ENDPOINT,
        options: Array.isArray(typeDef.options) ? typeDef.options : undefined,
        visibleWhen: visibleWhen && {
            field: visibleWhen.field,
            equals: visibleWhen.equals,
            in: Array.isArray(visibleWhen.in) ? visibleWhen.in.map(String) : undefined,
            notEmpty: Boolean(visibleWhen.notEmpty),
        },
        itemSchema: optionalObject<ListItemSchema>(typeDef.itemSchema),
        tabs: Array.isArray(typeDef.tabs) ? typeDef.tabs : undefined,
        inputElementRef: typeof typeDef.inputelementref === 'string' ? typeDef.inputelementref : undefined,
        required: typeDef.required !== false,
        defaultValue: parseDefaultValue(type, typeDef.default),
        iframe: type === 'iframe' ? optionalObject<CommandIframeConfig>(typeDef.iframe) : undefined,
        prefill: parseFieldPrefill(typeDef.prefill),
        fields: type === 'repeatable' ? optionalObject<Record<string, RepeatableField>>(typeDef.fields) : undefined,
    };
}

/** Every uploadFile step, including one nested in a forEach. */
function uploadSteps(steps: ReadonlyArray<WebAgentStep> | undefined): WebAgentStep[] {
    const found: WebAgentStep[] = [];
    const walk = (list: ReadonlyArray<WebAgentStep> | undefined) => {
        for (const step of list ?? []) {
            if (step.action === 'uploadFile') found.push(step);
            if (step.nestedSteps?.length) walk(step.nestedSteps);
        }
    };
    walk(steps);
    return found;
}

/** `${Photos}` and `${item.Photos}` both name the command field `Photos`. */
function uploadStepNamesField(step: WebAgentStep, fieldKey: string): boolean {
    const name = String(step.value ?? '').trim().replace(/^\$\{\s*([^}]+?)\s*\}$/, '$1').trim();
    return name === fieldKey || name === `item.${fieldKey}`;
}

/** Limits for one attachment field, taken from the uploadFile step whose value names that field. */
function uploadLimitsForField(steps: ReadonlyArray<WebAgentStep> | undefined, fieldKey: string) {
    const step = uploadSteps(steps).find((item) => uploadStepNamesField(item, fieldKey));
    // `accept` may be a list or a comma-separated string. Empty means every file type is allowed.
    const rawAccept = Array.isArray(step?.accept) ? step.accept : typeof step?.accept === 'string' ? step.accept.split(',') : [];
    const accept = rawAccept.map((item) => String(item ?? '').trim().replace(/^\./, '').toLowerCase()).filter(Boolean);
    const maxSizeMb = Number(step?.maxSizeMb);
    return {
        accept: accept.length ? accept : undefined,
        multiple: step?.multiple === true,
        maxSizeMb: Number.isFinite(maxSizeMb) && maxSizeMb > 0 ? maxSizeMb : undefined,
    };
}

/** Files picked in this browser session, so a thumbnail can still open after the form value is only metadata. */
const commandAttachmentFiles = new Map<string, File>();

type LocalAttachment = UploadedFileType & { s3_key?: string };

function toLocalAttachment(file: File, extra: Partial<LocalAttachment> = {}): LocalAttachment {
    return { size: formatFileSize(file.size), name: file.name, type: getFileType(file.name).toUpperCase(), uploading: false, file, ...extra };
}

type StoredAttachment = { filename: string; contentType: string; s3_key: string; source_id?: string; size?: number };

/** Files already stored on an attachment field. Accepts one object or a list. */
function readStoredAttachments(raw: string | undefined): StoredAttachment[] {
    const text = String(raw ?? '').trim();
    if (!text) return [];
    try {
        const parsed = JSON.parse(text) as unknown;
        const list = Array.isArray(parsed) ? parsed : [parsed];
        return list.flatMap((item) => {
            if (!item || typeof item !== 'object') return [];
            const record = item as Record<string, unknown>;
            const s3Key = typeof record.s3_key === 'string' ? record.s3_key.trim() : '';
            if (!s3Key) return [];
            const filename = typeof record.filename === 'string' && record.filename.trim()
                ? record.filename.trim()
                : 'attachment';
            return [{
                filename,
                contentType: typeof record.contentType === 'string' && record.contentType
                    ? record.contentType
                    : 'application/octet-stream',
                s3_key: s3Key,
                source_id: typeof record.source_id === 'string' ? record.source_id : undefined,
                size: typeof record.size === 'number' ? record.size : undefined,
            }];
        });
    } catch {
        return [];
    }
}

function attachmentValue(files: StoredAttachment[], multiple: boolean): string {
    if (!files.length) return '';
    return JSON.stringify(multiple ? files : files[0]);
}

/** Thumbnails for files still held in this browser session. */
function cachedLocalAttachments(stored: StoredAttachment[]): LocalAttachment[] {
    return stored.flatMap((item) => {
        const file = commandAttachmentFiles.get(item.s3_key);
        return file ? [toLocalAttachment(file, { source_id: item.source_id, s3_key: item.s3_key })] : [];
    });
}

/** Files already on screen for this field, or the cached thumbnails when the field was only just stored. */
function shownAttachments(local: LocalAttachment[] | undefined, stored: StoredAttachment[]): LocalAttachment[] {
    return local?.length ? local : cachedLocalAttachments(stored);
}

function parseFieldPrefill(value: unknown): FieldPrefill | undefined {
    const prefill = optionalObject<FieldPrefill>(value);
    return typeof prefill?.source === 'string' && typeof prefill.path === 'string' ? prefill : undefined;
}

/** Keys starting with `$` (e.g. `$sources`) configure the form rather than declare a field. */
export const parseFieldDefs = (commandJson: Record<string, any> | undefined | null): FieldDef[] =>
    commandJson && typeof commandJson === 'object'
        ? Object.entries(commandJson).filter(([key]) => !key.startsWith('$')).map(parseFieldDef)
        : [];

/**
 * Text form of every value. Values are text by contract, but they also arrive from stored
 * conversations and from embedded templates, so any other json type is turned into text here
 * rather than left to break the string handling every field renderer and validator relies on.
 */
function normalizeCommandFormValue(value: unknown): string {
    if (typeof value === 'string') return value;
    if (value == null) return '';
    return JSON.stringify(value) ?? '';
}

export const normalizeCommandFormData = (
    formData: Record<string, unknown>,
): Record<string, string> => Object.fromEntries(
    Object.entries(formData ?? {}).map(([key, value]) => [
        key,
        normalizeCommandFormValue(value),
    ]),
);

type ValidationResult = { valid: true } | { valid: false; errorKey: string };

/** The item fields of a `repeatable` field, or null for every other type. */
function repeatableFields(fd: FieldDef): Record<string, RepeatableField> | null {
    if (fd.type !== 'repeatable' || !fd.fields) return null;
    return Object.keys(fd.fields).length ? fd.fields : null;
}

function parseRepeatableItems(raw: string | undefined): Record<string, string>[] {
    if (!raw?.trim()) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((item) => item && typeof item === 'object' && !Array.isArray(item));
    } catch {
        return [];
    }
}

/** The name inside a `${name}` placeholder, or '' when the value isn't one. */
function placeholderName(value: unknown): string {
    if (typeof value !== 'string') return '';
    const text = value.trim();
    if (!text.startsWith('${') || !text.endsWith('}')) return '';
    const name = text.slice(2, -1).trim();
    return name && !name.includes('}') ? name : '';
}

/** Child repeatable field key → the repeatable field it follows, from forEach steps that set dependsOnStepId. */
function repeatableFollows(steps: ReadonlyArray<WebAgentStep> | undefined): Record<string, string> {
    const byId = new Map((steps ?? []).filter((step) => step.id).map((step) => [step.id as string, step]));
    const links: Record<string, string> = {};
    for (const step of steps ?? []) {
        if (step.action !== 'forEach' || !step.dependsOnStepId) continue;
        const parent = byId.get(step.dependsOnStepId);
        const childKey = placeholderName(step.value);
        const parentKey = placeholderName(parent?.value);
        if (parent?.action === 'forEach' && childKey && parentKey) links[childKey] = parentKey;
    }
    return links;
}

function repeatableFieldFilled(def: RepeatableField | undefined, value: string | undefined): boolean {
    if (def?.required === false) return true;
    if (def?.type === 'attachment') return readStoredAttachments(value).length > 0;
    return String(value ?? '').trim() !== '';
}

function repeatableItemsComplete(fields: Record<string, RepeatableField>, items: Record<string, string>[]): boolean {
    return items.every((item) => Object.entries(fields).every(([key, def]) => repeatableFieldFilled(def, item?.[key])));
}

/** One item of an `inspectionItems` field, stored in the form as a JSON array. `localId` only
 *  keys the editor's rows and is left out of the payload. */
export type CommandInspectionItem = InspectionChecklistItem & {
    /** SOR record id; absent for an item added in the form */
    id?: string;
    itemKey?: string;
    /** `question.evidencePolicy.allowedKind` of the item, e.g. `PHOTO` */
    allowedKind?: string;
};

function parseInspectionItems(raw: string | undefined): CommandInspectionItem[] {
    if (!raw?.trim()) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .filter((item) => item && typeof item === 'object' && !Array.isArray(item))
            .map((item, index) => ({
                ...item,
                localId: String(item.localId ?? item.id ?? `item-${index}`),
                title: typeof item.title === 'string' ? item.title : '',
                mediaType: isInspectionMediaType(item.mediaType) ? item.mediaType : 'photo',
                required: item.required === true,
            }));
    } catch {
        return [];
    }
}

function serializeInspectionItems(items: CommandInspectionItem[]): string {
    return JSON.stringify(items.map((item) => ({
        ...item,
        allowedKind: MEDIA_TYPE_TO_EVIDENCE_KIND[item.mediaType],
    })));
}

/** A `slm.insp.inspection_template_item` record as an editor row, read the same way as the
 *  Inspection Templates settings screen reads it. */
function inspectionItemFromRecord(record: Record<string, any>): CommandInspectionItem {
    const data = record.data ?? {};
    return {
        localId: String(record.id),
        id: record.id,
        itemKey: data.itemKey,
        title: String(data.title ?? record.display ?? ''),
        mediaType: getMediaType(data),
        required: data.question?.required === true,
    };
}

function inspectionItemsFromRecords(records: Record<string, any>[]): CommandInspectionItem[] {
    return records
        // Sections only group the checklist
        .filter((record) => record?.data?.itemType !== 'SECTION')
        .sort((a, b) => (a.data?.position ?? 0) - (b.data?.position ?? 0)
            || String(a.created_at ?? '').localeCompare(String(b.created_at ?? '')))
        .map(inspectionItemFromRecord);
}

const inspectionItemsNamed = (items: CommandInspectionItem[]): boolean =>
    items.every((item) => item.title.trim() !== '');

/** Whether a required field has a usable value: a filled list for `repeatable` and
 *  `inspectionItems`, any text otherwise. */
function isFieldFilled(fd: FieldDef, formData: Record<string, string>): boolean {
    const fields = repeatableFields(fd);
    if (fields) {
        const items = parseRepeatableItems(formData[fd.key]);
        return items.length > 0 && repeatableItemsComplete(fields, items);
    }
    if (fd.type === 'inspectionItems') {
        const items = parseInspectionItems(formData[fd.key]);
        return items.length > 0 && inspectionItemsNamed(items);
    }
    if (fd.type === 'attachment') return readStoredAttachments(formData[fd.key]).length > 0;
    const v = formData[fieldValueKey(fd)];
    return v !== undefined && v.trim() !== '';
}

/** A visible list with an item the user hasn't finished, which blocks sending even when optional. */
function hasIncompleteListItem(fd: FieldDef, formData: Record<string, string>): boolean {
    if (!isVisibleField(fd, formData)) return false;
    if (fd.type === 'inspectionItems') return !inspectionItemsNamed(parseInspectionItems(formData[fd.key]));
    const fields = repeatableFields(fd);
    if (!fields) return false;
    const items = parseRepeatableItems(formData[fd.key]);
    return items.length > 0 && !repeatableItemsComplete(fields, items);
}

/** Validates that every required visible field is filled and that a message exists for the 'custom' dropdown option. */
export const validateCommandForm = (
    commandJson: Record<string, any>,
    rawFormData: Record<string, string>,
    customMessage: string,
): ValidationResult => {
    const formData = normalizeCommandFormData(rawFormData);
    const fieldDefs = parseFieldDefs(commandJson);
    const allFilled = fieldDefs.filter((fd) => isRequiredField(fd, formData)).every((fd) => isFieldFilled(fd, formData));
    const partialList = fieldDefs.some((fd) => hasIncompleteListItem(fd, formData));
    if (!allFilled || partialList) return { valid: false, errorKey: 'Please fill all form fields' };
    const hasCustomDropdown = fieldDefs.some(
        (fd) => fd.type === 'dropdown' && formData[fd.key] === CUSTOM_DROPDOWN_KEY,
    );
    if (hasCustomDropdown && !customMessage?.trim()) {
        return { valid: false, errorKey: 'Please enter a message for custom option' };
    }
    return { valid: true };
};

interface PayloadValueKeys {
    numberKeys: Set<string>;
    jsonKeys: Set<string>;
    inspectionItemKeys: Set<string>;
}

/** One trimmed form value as it is sent in `form_data`. */
function payloadValue(key: string, trimmed: string, keys: PayloadValueKeys): unknown {
    if (keys.inspectionItemKeys.has(key)) {
        return parseInspectionItems(trimmed).map(({ localId: _localId, ...item }) => item);
    }
    if (keys.numberKeys.has(key)) {
        const n = Number(trimmed);
        return Number.isNaN(n) ? trimmed : n;
    }
    if (keys.jsonKeys.has(key)) {
        try {
            return JSON.parse(trimmed);
        } catch {
            return trimmed;
        }
    }
    return trimmed;
}

/** Builds the command-form portion of the dify_stream payload (`form_data` + optional `user_input`). */
export const buildCommandPayloadFields = (
    commandJson: Record<string, any>,
    formData: Record<string, string>,
    customMessage: string,
    userInput?: string,
): Record<string, any> => {
    const fieldDefs = parseFieldDefs(commandJson);
    const keys: PayloadValueKeys = {
        numberKeys: new Set(fieldDefs.filter((fd) => fd.type === 'number').map((fd) => fd.key)),
        // An embedded template reports its output as json, which is sent on as structured data.
        jsonKeys: new Set(fieldDefs.filter((fd) => fd.type === 'iframe' || fd.type === 'repeatable' || fd.type === 'attachment').map(fieldValueKey)),
        inspectionItemKeys: new Set(fieldDefs.filter((fd) => fd.type === 'inspectionItems').map((fd) => fd.key)),
    };
    const form_data: Record<string, any> = {};
    for (const [k, v] of Object.entries(normalizeCommandFormData(formData))) {
        const trimmed = v.trim();
        if (trimmed) form_data[k] = payloadValue(k, trimmed, keys);
    }
    if (customMessage?.trim()) {
        form_data.message = customMessage.trim();
    }
    const payload: Record<string, any> = { meta_data: { form_data } };
    if (userInput?.trim()) {
        payload.user_input = userInput.trim();
    }
    return payload;
};

const DEFAULT_SOR_TOKEN_KEY = 'sor_token';

/**
 * The `meta_data` key the signed-in user's SOR access token is sent under, or null when the
 * command doesn't ask for it. Opted into by the command json's `$sorToken`: `true` sends it as
 * `sor_token`, `{ "key": "<name>" }` under another name, so the agent's workflow can call SOR
 * as the user.
 */
export function commandSorTokenKey(commandJson: Record<string, any> | undefined | null): string | null {
    const config = commandJson?.$sorToken;
    if (config === true) return DEFAULT_SOR_TOKEN_KEY;
    if (!config || typeof config !== 'object' || config.enabled === false) return null;
    return typeof config.key === 'string' && config.key.trim() ? config.key.trim() : DEFAULT_SOR_TOKEN_KEY;
}

/**
 * `buildCommandPayloadFields`, plus the app-wide SOR access token when the command json's
 * `$sorToken` asks for it and the tenant has SOR active. Rejects when the token can't be
 * obtained, so the command isn't sent without it.
 */
export function useCommandPayloadBuilder() {
    const { getSorToken } = useSorApi();
    return useCallback(async (
        commandJson: Record<string, any>,
        formData: Record<string, string>,
        customMessage: string,
        userInput?: string,
    ): Promise<Record<string, any>> => {
        const payload = buildCommandPayloadFields(commandJson, formData, customMessage, userInput);
        const tokenKey = commandSorTokenKey(commandJson);
        // The tenant settings turn SOR off; until they load the token is still sent
        if (tokenKey && isSorEnabled() !== false) payload.meta_data[tokenKey] = await getSorToken();
        return payload;
    }, [getSorToken]);
}

/** Extracts and validates the `command` attribute from an aiworker's aiworkers_attrs array. */
export const extractCommandJsonFromAttrs = (
    aiworkersAttrs: any[] | null | undefined,
): Record<string, any> | undefined => {
    if (!Array.isArray(aiworkersAttrs)) return undefined;
    const attr = aiworkersAttrs.find((a: any) => a?.attr_name === 'command');
    if (!attr) return undefined;
    let parsed = attr.attr_json;
    if (parsed == null && typeof attr.attr_value === 'string') {
        try { parsed = JSON.parse(attr.attr_value); } catch { parsed = null; }
    }
    if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
        return parsed as Record<string, any>;
    }
    return undefined;
};

/** Resolves placeholders without relying on i18next interpolation (PAL/extension don't init i18next). */
const buildFieldLabel = (key: string): string => formatKeyForDisplay(key);

/** A field's display text: the json's `label` when it sets one, otherwise derived from the key. */
const fieldLabel = (fd: FieldDef): string => fd.label ?? formatKeyForDisplay(fd.key);

export type CommandPrefillResolver = (
    fields: Array<{ key: string; ref: string }>,
    ctx?: { aiworkerId?: string },
) => Promise<Record<string, string>>;

let _commandPrefillResolver: CommandPrefillResolver | null = null;

/** Host apps (e.g. the PAL extension) register a resolver that reads values from the host page. */
export const setCommandPrefillResolver = (fn: CommandPrefillResolver | null): void => {
    _commandPrefillResolver = fn;
};

export const getCommandPrefillResolver = (): CommandPrefillResolver | null => _commandPrefillResolver;

export interface JsonFormatButtonProps {
    /** Current raw JSON string value. */
    value: string;
    /** Called with the prettified JSON string when formatting succeeds. */
    onChange: (formatted: string) => void;
    /** Disable the button when the value is empty or already known to be invalid. */
    disabled?: boolean;
}

/**
 * A small inline button that pretty-prints the JSON string in `value`.
 * Silently no-ops if parsing fails (invalid JSON). Intended to sit inside a
 * Textarea `label` slot next to a label string.
 */
export function JsonFormatButton({ value, onChange, disabled = false }: Readonly<JsonFormatButtonProps>) {
    const { t } = useTranslation();
    return (
        <button
            type="button"
            className="text-xs text-default-500 enabled:hover:bg-default-100 disabled:text-default-300 flex items-center justify-center gap-1 rounded-sm p-0.5"
            disabled={disabled || !value.trim()}
            onClick={() => {
                try {
                    onChange(JSON.stringify(JSON.parse(value), null, 2));
                } catch { /* invalid json, skip */ }
            }}
        >
            <Icon icon="material-symbols:format-align-right-rounded" className="w-4 h-4" />
            <span>{t('Format')}</span>
        </button>
    );
}

type ListFieldState = { states: boolean[]; extra: Record<string, any>[] };

/** Parses a `list` field's combined state — per-item toggle states plus any ad-hoc items the
 *  user has added — out of a *single* `formData[fd.key]` JSON blob. Everything for a list field
 *  is read/written as one blob (never two separate `onFieldChange` calls for the same
 *  interaction) because `onFieldChange` merges against a `formData` snapshot that hasn't
 *  re-rendered yet: two sequential calls in one event handler would have the second overwrite
 *  the first's change instead of combining with it. */
function parseListFieldState(raw: string | undefined): ListFieldState {
    try {
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed && Array.isArray(parsed.extra)) {
            return { states: Array.isArray(parsed.states) ? parsed.states : [], extra: parsed.extra };
        }
    } catch { /* ignore malformed state */ }
    return { states: [], extra: [] };
}

/** Resolves which icon (if any) to show for a checklist item. Explicit `itemSchema.iconField`
 *  on the item wins; otherwise the item's `typeField` value (e.g. `mediaType: "video"`) is
 *  looked up against `itemSchema.addOptions[].icon` — the same list the "+ Add" chooser uses —
 *  so the icon shown for a given type is entirely configuration-driven, for both
 *  source-provided and user-added items, with no type-specific code. Falls back to
 *  `itemSchema.defaultIcon` (or no icon at all) when nothing resolves, rather than assuming
 *  every list is a media checklist. */
function resolveItemIcon(item: Record<string, any>, itemSchema: ListItemSchema | undefined): string | undefined {
    const iconField = itemSchema?.iconField;
    if (iconField && item[iconField]) return item[iconField];
    const typeField = itemSchema?.typeField ?? 'mediaType';
    const typeValue = item[typeField];
    const matched = itemSchema?.addOptions?.find((opt) => opt.value === typeValue);
    return matched?.icon ?? itemSchema?.defaultIcon;
}

type DropdownOption = { key: string; label: string; value?: string };
type DropdownState = {
    dropdownOptions: Record<string, DropdownOption[]>;
    dropdownLoading: Record<string, boolean>;
    /** Raw records behind each field's options (localStorage rows, api rows), keyed the same way
     *  as `dropdownOptions`. `list` fields with a `ref` source read from this to pull `path` (e.g.
     *  the selected Inspection Template's `items`) off the record matching the referenced field's value. */
    dropdownRecords: Record<string, Record<string, any>[]>;
};

export type CommandApiSourceResolver = (endpoint: string) => Promise<Record<string, any>[]>;

let _commandApiSourceResolver: CommandApiSourceResolver | null = null;

/** Host apps register a resolver for `source: {kind:'api', endpoint}` fields not covered by the
 *  built-in `configuration-actions` endpoint (e.g. a customer-configured REST lookup). */
export const setCommandDataSourceResolver = (fn: CommandApiSourceResolver | null): void => {
    _commandApiSourceResolver = fn;
};

type PathSegment = [segment: string, bracket: string | undefined];

/** Splits a path into dotted segments and bracketed ones: `items[0]`, or `cookies[name=.ASPXAUTH]`
 *  for the first array element whose `name` equals the text after `=` (brackets may hold dots).
 *  A single pass, so a long configured path can't slow down. */
function splitPath(path: string): PathSegment[] {
    const segments: PathSegment[] = [];
    let index = 0;
    while (index < path.length) {
        const char = path[index];
        const close = char === '[' ? path.indexOf(']', index + 1) : -1;
        if (close >= 0) {
            segments.push([path.slice(index, close + 1), path.slice(index + 1, close)]);
            index = close + 1;
        } else if (char === '.' || char === '[' || char === ']') {
            index += 1;
        } else {
            let end = index;
            while (end < path.length && !'.[]'.includes(path[end])) end += 1;
            segments.push([path.slice(index, end), undefined]);
            index = end;
        }
    }
    return segments;
}

/** `'Browser Data'` / `"Browser Data"` → `Browser Data`, so bracket keys may contain spaces or dots. */
function unquotePathKey(key: string): string {
    const trimmed = key.trim();
    return /^(['"]).*\1$/u.test(trimmed) ? trimmed.slice(1, -1) : trimmed;
}

function readPathSegment(value: any, segment: string, bracket: string | undefined): any {
    if (!value || typeof value !== 'object') return undefined;
    const eq = bracket?.indexOf('=') ?? -1;
    if (bracket === undefined || eq <= 0 || !Array.isArray(value)) return value[bracket === undefined ? segment : unquotePathKey(bracket)];
    const field = unquotePathKey(bracket.slice(0, eq));
    const expected = unquotePathKey(bracket.slice(eq + 1));
    return value.find((item) => item && typeof item === 'object' && String(item[field]) === expected);
}

function readPathSegments(value: any, segments: PathSegment[], index: number): any {
    if (index >= segments.length || value === undefined) return value;
    const [segment, bracket] = segments[index];
    // `[*]` reads the rest of the path from every item, keeping the items where it resolves.
    if (bracket?.trim() === '*') {
        if (!Array.isArray(value)) return undefined;
        return value.map((item) => readPathSegments(item, segments, index + 1)).filter((item) => item !== undefined);
    }
    return readPathSegments(readPathSegment(value, segment, bracket), segments, index + 1);
}

/** Reads a dot path. Supports `a.b`, `a['key with spaces']`, `list[field=value]` and `list[*].field`. */
function readByPath(record: Record<string, any>, path?: string): any {
    if (!path) return record;
    return readPathSegments(record, splitPath(path), 0);
}

/** A record's own key when it has one (so existing configs read exactly as before), otherwise a
 *  dot path such as `data.category` for nested records like SOR's. */
function readField(record: Record<string, any>, field: string): any {
    if (!record || typeof record !== 'object') return undefined;
    return field in record ? record[field] : readByPath(record, field);
}

const TEMPLATE_VALUE_PATTERN = /\{\{([^{}]+)\}\}/gu;

/** Resolves `{{dot.path}}` values in configured endpoints and request bodies. */
function resolveConfiguredValue(value: unknown, context: Record<string, any>): unknown {
    if (Array.isArray(value)) return value.map((item) => resolveConfiguredValue(item, context));
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>)
                .map(([key, item]) => [key, resolveConfiguredValue(item, context)]),
        );
    }
    if (typeof value !== 'string') return value;

    const exact = /^\{\{([^{}]+)\}\}$/u.exec(value);
    if (exact) return readByPath(context, exact[1].trim());

    return value.replace(TEMPLATE_VALUE_PATTERN, (_match, path: string) => {
        const resolved = readByPath(context, path.trim());
        return resolved == null ? '' : String(resolved);
    });
}

/**
 * Derives a stable, unique, non-empty option key per record. Guards against malformed/legacy
 * source data (e.g. a stale localStorage row missing its value field) producing blank or
 * duplicate keys, which otherwise corrupts NextUI's Autocomplete/Listbox internal selection and
 * focus state (freezes selection, breaks clearing, and can make an unrelated dialog's close
 * button feel unresponsive because the browser is busy re-resolving the ambiguous key).
 */
function deriveOptionKeys(records: Record<string, any>[], valueField: string): string[] {
    const seen = new Set<string>();
    return records.map((r, idx) => {
        let key = String(readField(r, valueField) ?? '').trim();
        if (!key) key = `item-${idx}`;
        while (seen.has(key)) key = `${key}-${idx}`;
        seen.add(key);
        return key;
    });
}

function readPicked(record: Record<string, any>, pick: SourcePick): string {
    const list = record[pick.list];
    if (!Array.isArray(list)) return '';
    const match = list.find((item) => (
        item && typeof item === 'object'
        && Object.entries(pick.where).every(([key, expected]) => item[key] === expected)
    ));
    const raw = match?.[pick.value];
    return typeof raw === 'string' ? raw.trim() : '';
}

/** The value a `when` filter compares against the other field's selection. */
function readWhenValue(record: Record<string, any>, when: NonNullable<FieldSource['when']>): string {
    if (when.pick) return readPicked(record, when.pick);
    const raw = when.valueField ? readField(record, when.valueField) : undefined;
    return raw == null ? '' : String(raw).trim();
}

function uniqueRecords(records: Record<string, any>[], source: FieldSource): Record<string, any>[] {
    const valueField = source.valueField ?? 'code';
    const seen = new Set<string>();
    const next: Record<string, any>[] = [];
    for (const record of records) {
        const key = source.pick ? readPicked(record, source.pick) : String(readField(record, valueField) ?? '').trim();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        next.push(record);
    }
    return next;
}

function recordsToOptions(records: Record<string, any>[], source: FieldSource | undefined): DropdownOption[] {
    const rows = source?.distinct ? uniqueRecords(records, source) : records;
    if (source?.pick) {
        const picked = rows.flatMap((record) => {
            const key = readPicked(record, source.pick as SourcePick);
            return key ? [{ record, key }] : [];
        });
        const keys = deriveOptionKeys(picked.map(({ key }) => ({ code: key })), 'code');
        return picked.map(({ record, key }, idx) => {
            const rawLabel = source.labelField ? readField(record, source.labelField) : key;
            return {
                key: keys[idx],
                label: typeof rawLabel === 'string' && rawLabel.trim() ? rawLabel : keys[idx],
                value: typeof record.value === 'string' ? record.value : undefined,
            };
        });
    }
    const valueField = source?.valueField ?? 'code';
    const labelField = source?.labelField ?? 'name';
    const keys = deriveOptionKeys(rows, valueField);
    return rows.map((r, idx) => {
        const label = readField(r, labelField);
        return {
            key: keys[idx],
            label: typeof label === 'string' ? label : keys[idx],
            // Only the legacy action dropdown reads this, to prefill its message textarea.
            value: typeof r.value === 'string' ? r.value : undefined,
        };
    });
}

/** Options for one dropdown. `when` narrows the fetched records using the current form value. */
function dropdownChoices(
    fd: FieldDef,
    formData: Record<string, string>,
    dropdownOptions: Record<string, DropdownOption[]>,
    dropdownRecords: Record<string, Record<string, any>[]>,
): DropdownOption[] {
    const when = fd.source?.when;
    if (!when) return dropdownOptions[fd.key] ?? [];
    const selected = (formData[when.field] ?? '').trim();
    if (!selected) return [];
    const matched = (dropdownRecords[fd.key] ?? []).filter(
        (record) => readWhenValue(record, when) === selected,
    );
    return recordsToOptions(matched, fd.source);
}

/** Copies form data only when a key actually changes, and only once per edit chain. */
function withCommandFormValue(
    original: Record<string, string>,
    current: Record<string, string>,
    key: string,
    value: string,
): Record<string, string> {
    if ((current[key] ?? '') === value) return current;
    const next = current === original ? { ...original } : current;
    next[key] = value;
    return next;
}

function isLegacyActionDropdown(fd: FieldDef): boolean {
    return fd.type === 'dropdown' && fd.legacyActionDropdown === true;
}

function clearStaleCommandSelections(
    fieldDefs: FieldDef[],
    formData: Record<string, string>,
    dropdownRecords: Record<string, Record<string, any>[]>,
    dropdownLoading: Record<string, boolean>,
): Record<string, string> {
    let next = formData;
    for (const fd of fieldDefs) {
        const when = fd.source?.when;
        if (fd.type !== 'dropdown' || !when || dropdownLoading[fd.key]) continue;
        if (!dropdownRecords[fd.key]) continue;
        const selected = (next[fd.key] ?? '').trim();
        if (!selected) continue;
        const allowed = dropdownChoices(fd, next, {}, dropdownRecords);
        if (!allowed.some((option) => option.key === selected)) next = withCommandFormValue(formData, next, fd.key, '');
    }
    for (const fd of fieldDefs) {
        const dependsOn = fd.iframe?.dependsOn;
        if (fd.type !== 'iframe' || !dependsOn?.length) continue;
        const waitingOnFilteredField = dependsOn.some((dep) => (
            fieldDefs.some((field) => field.key === dep && field.source?.when)
            && !(next[dep] ?? '').trim()
        ));
        if (waitingOnFilteredField) next = withCommandFormValue(formData, next, fieldValueKey(fd), '');
    }
    return next;
}

/** Selects the only choice in a visible dropdown. A legacy action dropdown also offers Custom, so it
 *  is left alone unless Custom is its only choice. */
function selectLoneDropdownOptions(
    fieldDefs: FieldDef[],
    formData: Record<string, string>,
    dropdownOptions: Record<string, DropdownOption[]>,
    dropdownRecords: Record<string, Record<string, any>[]>,
    dropdownLoading: Record<string, boolean>,
): Record<string, string> {
    let next = formData;
    for (const fd of fieldDefs) {
        if (fd.type !== 'dropdown' || dropdownLoading[fd.key] || !isVisibleField(fd, next)) continue;
        if (!(fd.key in dropdownOptions) && !(fd.key in dropdownRecords)) continue;
        const dependsOnField = fd.source?.when?.field;
        if (dependsOnField && !(next[dependsOnField] ?? '').trim()) continue;
        const options = dropdownChoices(fd, next, dropdownOptions, dropdownRecords);
        const choices = isLegacyActionDropdown(fd) ? [...options, { key: CUSTOM_DROPDOWN_KEY, label: '' }] : options;
        if (choices.length !== 1) continue;
        next = withCommandFormValue(formData, next, fd.key, choices[0].key);
    }
    return next;
}

/** Reads a `static`/`localStorage` sourced field synchronously (no network/loading state needed). */
function resolveSyncSource(fd: FieldDef): { options: DropdownOption[]; records: Record<string, any>[] } | null {
    if (fd.options && (!fd.source || fd.source.kind === 'static')) {
        return { options: fd.options.map((o) => ({ key: o.value, label: o.label })), records: fd.options.map((o) => ({ code: o.value, name: o.label })) };
    }
    if (fd.source?.kind === 'localStorage' && fd.source.key) {
        try {
            const raw = typeof window !== 'undefined' ? window.localStorage.getItem(fd.source.key) : null;
            const records = raw ? JSON.parse(raw) : [];
            if (!Array.isArray(records)) return { options: [], records: [] };
            return { options: recordsToOptions(records, fd.source), records };
        } catch {
            return { options: [], records: [] };
        }
    }
    return null;
}

type QueryTrigger = (args: any) => { unwrap: () => Promise<any> };

function configurationRecords(response: any): Record<string, any>[] {
    const botData = response?.configurations?.[0]?.config?.bot_data as Record<string, unknown> | undefined;
    if (!botData || typeof botData !== 'object') return [];
    return Object.keys(botData)
        .filter((key) => !EXCLUDED_BOT_DATA_KEYS.has(key))
        .map((key) => ({
            code: key,
            name: formatKeyForDisplay(key),
            value: typeof botData[key] === 'string' ? botData[key] : '',
        }));
}

async function loadApiSourceRecords(
    endpoint: string,
    aiworkerId: string | undefined,
    queryConfiguration: QueryTrigger,
    queryCommandSource: QueryTrigger,
): Promise<Record<string, any>[] | null> {
    if (endpoint === LEGACY_API_ENDPOINT) {
        if (!aiworkerId) return null;
        const response = await queryConfiguration({
            scope: 'tenant',
            pagination: { page: 1, per_page: 10 },
            filters: {
                aiworker_id: aiworkerId,
                config_name: 'agent_config',
                status: 'active',
                is_active: true,
            },
        }).unwrap();
        return configurationRecords(response);
    }
    if (endpoint.startsWith('/')) return queryCommandSource(endpoint).unwrap();
    return _commandApiSourceResolver?.(endpoint) ?? [];
}

type SorQueryRecords = ReturnType<typeof useSorApi>['queryRecords'];

/** The records of a `sor` source, read with one query and no paging. */
async function loadSorSourceRecords(
    queryRecords: SorQueryRecords,
    definition: string,
    filter: SorFilter | null | undefined,
    signal?: AbortSignal,
): Promise<Record<string, any>[]> {
    const response = await queryRecords<SorQueryResponse>({
        definition,
        filter: filter ?? null,
        include_deleted: false,
    }, signal);
    return response?.items ?? [];
}

const isSorSource = (source: FieldSource | undefined): boolean =>
    source?.kind === 'sor' && Boolean(source.definition);

function setFieldsLoading(
    setter: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
    keys: string[],
    loading: boolean,
): void {
    setter((previous) => ({
        ...previous,
        ...Object.fromEntries(keys.map((key) => [key, loading])),
    }));
}

/**
 * Resolves dropdown/radio/list option data for every field that declares a `source`. `static` and
 * `localStorage` sources resolve synchronously; `api` sources (including the legacy
 * `configuration-actions` endpoint) are fetched once per (aiworkerId + field signature) even when
 * `commandJson` reference changes on every parent render (root cause of a prior infinite-loop bug).
 * The `t` function is intentionally excluded from deps because hosts like PAL do not initialize
 * i18next, returning an unstable `t` every render.
 */
function useCommandFormDropdowns(
    aiworkerId: string | undefined,
    commandJson: Record<string, any> | undefined,
    enabled: boolean,
): DropdownState {
    const { t } = useTranslation();
    const [apiRecords, setApiRecords] = useState<Record<string, Record<string, any>[]>>({});
    const [dropdownLoading, setDropdownLoading] = useState<Record<string, boolean>>({});
    const [queryConfiguration] = useLazyGetAiWorkerConfigurationsQuery();
    const [queryCommandSource] = useLazyGetCommandSourceRecordsQuery();
    const { queryRecords } = useSorApi();
    // One request per SOR query for the life of the form: fields reading the same query (Type and
    // Inspection Template) share it, and so does a re-run of the effect below (StrictMode, or
    // `aiworkerId` arriving after the first render). A failed request is dropped so it can retry.
    const sorRequestsRef = useRef(new Map<string, Promise<Record<string, any>[]>>());

    const fieldDefs = useMemo(() => parseFieldDefs(commandJson), [commandJson]);

    const syncState = useMemo(() => {
        const options: Record<string, DropdownOption[]> = {};
        const records: Record<string, Record<string, any>[]> = {};
        for (const fd of fieldDefs) {
            const resolved = resolveSyncSource(fd);
            if (resolved) {
                options[fd.key] = resolved.options;
                records[fd.key] = resolved.records;
            }
        }
        return { options, records };
    }, [fieldDefs]);

    // An `inspectionItems` field reads its own `sor` source, since its query follows the form.
    const apiFields = useMemo(
        () => fieldDefs.filter((fd) => (
            (fd.source?.kind === 'api' && fd.source.endpoint)
            || (fd.type !== 'inspectionItems' && isSorSource(fd.source))
        )),
        [fieldDefs],
    );

    const apiFieldsSig = useMemo(
        () => apiFields
            .map((fd) => (fd.source?.kind === 'sor'
                ? `${fd.key}\u0000sor\u0000${JSON.stringify([fd.source.definition, fd.source.filter ?? null])}`
                : `${fd.key}\u0000${fd.source?.endpoint}`))
            .sort((a, b) => a.localeCompare(b))
            .join('|'),
        [apiFields],
    );

    useEffect(() => {
        if (!enabled || !apiFieldsSig) return;
        const entries = apiFields.map((fd) => ({ key: fd.key, endpoint: fd.source?.endpoint ?? '', source: fd.source }));
        let cancelled = false;
        const loadSor = (source: FieldSource) => {
            const sorRequests = sorRequestsRef.current;
            const requestKey = JSON.stringify([source.definition, source.filter ?? null]);
            let request = sorRequests.get(requestKey);
            if (!request) {
                request = loadSorSourceRecords(queryRecords, source.definition ?? '', source.filter);
                request.catch(() => sorRequests.delete(requestKey));
                sorRequests.set(requestKey, request);
            }
            return request;
        };

        setFieldsLoading(setDropdownLoading, entries.map(({ key }) => key), true);

        void (async () => {
            for (const { key, endpoint, source } of entries) {
                try {
                    const records = source?.kind === 'sor'
                        ? await loadSor(source)
                        : await loadApiSourceRecords(endpoint, aiworkerId, queryConfiguration, queryCommandSource);
                    if (cancelled) return;
                    if (records) setApiRecords((prev) => ({ ...prev, [key]: records }));
                } catch {
                    if (!cancelled) CtyShowToast(checkType.error, t('Failed to load dropdown options'));
                }
            }
            if (!cancelled) setFieldsLoading(setDropdownLoading, entries.map(({ key }) => key), false);
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the stable `apiFieldsSig`; `t` & the query hooks are intentionally omitted; PAL/extension don't init i18next so `t` is unstable and would cause an infinite refetch loop.
    }, [enabled, aiworkerId, apiFieldsSig]);

    // Mapping records -> options lives outside the fetch effect so a change to the json's
    // `valueField`/`labelField` alone re-labels the existing records without a refetch.
    const apiOptions = useMemo(() => {
        const options: Record<string, DropdownOption[]> = {};
        for (const [key, records] of Object.entries(apiRecords)) {
            options[key] = recordsToOptions(records, fieldDefs.find((fd) => fd.key === key)?.source);
        }
        return options;
    }, [apiRecords, fieldDefs]);

    const dropdownOptions = useMemo(
        () => ({ ...syncState.options, ...apiOptions }),
        [syncState.options, apiOptions],
    );
    const dropdownRecords = useMemo(
        () => ({ ...syncState.records, ...apiRecords }),
        [syncState.records, apiRecords],
    );

    return { dropdownOptions, dropdownLoading, dropdownRecords };
}

/** Computes the empty-only subset of resolved host values that may be applied to the form. */
function computePrefillUpdates(
    resolved: Record<string, string>,
    current: Record<string, string>,
    fieldDefByKey: Map<string, FieldDef>,
    dropdownOptions: Record<string, DropdownOption[]>,
): Record<string, string> {
    const applied: Record<string, string> = {};
    for (const [key, rawValue] of Object.entries(resolved)) {
        const value = typeof rawValue === 'string' ? rawValue.trim() : '';
        const fd = fieldDefByKey.get(key);
        if (!value || current[key]?.trim() || !fd) continue;
        // Dropdown prefill only applies when the value matches an already-loaded option. Because
        // prefill runs once, a dropdown whose options arrive after the resolver resolves will not
        // be filled. This is acceptable today since inputelementref is used for string/number/hidden
        // fields; revisit (add a "dropdowns loaded" retry signal) if host dropdown prefill is needed.
        if (fd.type === 'dropdown' && !(dropdownOptions[key] ?? []).some((o) => o.key === value)) continue;
        applied[key] = value;
    }
    return applied;
}

/**
 * Prefills editable command-form fields that declare an `inputelementref`, using a host-registered
 * resolver (PAL extension). Runs once per (aiworkerId + field signature), only fills empty fields
 * (never clobbers user edits / rerun values), and is a no-op when no resolver is registered
 * (e.g. the main web app).
 */
function useCommandPrefill(
    commandJson: Record<string, any> | undefined,
    formData: Record<string, string>,
    onFormDataChange: ((data: Record<string, string>) => void) | undefined,
    opts: { aiworkerId?: string; enabled: boolean; dropdownOptions: Record<string, DropdownOption[]> },
): void {
    const { aiworkerId, enabled, dropdownOptions } = opts;

    const formDataRef = useRef(formData);
    formDataRef.current = formData;
    const onChangeRef = useRef(onFormDataChange);
    onChangeRef.current = onFormDataChange;
    const dropdownOptionsRef = useRef(dropdownOptions);
    dropdownOptionsRef.current = dropdownOptions;

    const refFields = useMemo(
        () => parseFieldDefs(commandJson).filter((fd) => !!fd.inputElementRef),
        [commandJson],
    );

    const signature = useMemo(() => {
        const fieldSig = refFields
            .map((fd) => `${fd.key}=${fd.inputElementRef}`)
            .sort((a, b) => a.localeCompare(b))
            .join('|');
        return `${aiworkerId ?? ''}::${fieldSig}`;
    }, [aiworkerId, refFields]);

    const ranSignatureRef = useRef<string | null>(null);

    useEffect(() => {
        if (!enabled || refFields.length === 0) return;
        const resolver = getCommandPrefillResolver();
        if (!resolver) return;
        if (ranSignatureRef.current === signature) return;
        ranSignatureRef.current = signature;

        let cancelled = false;
        let completed = false;
        const fieldDefByKey = new Map(refFields.map((fd) => [fd.key, fd]));

        void (async () => {
            try {
                const resolved = await resolver(
                    refFields.map((fd) => ({ key: fd.key, ref: fd.inputElementRef ?? '' })),
                    { aiworkerId },
                );
                if (cancelled || !resolved) return;

                const applied = computePrefillUpdates(
                    resolved,
                    formDataRef.current,
                    fieldDefByKey,
                    dropdownOptionsRef.current,
                );

                if (Object.keys(applied).length > 0 && onChangeRef.current) {
                    onChangeRef.current({ ...formDataRef.current, ...applied });
                }
                completed = true;
            } catch {
                ranSignatureRef.current = null;
            }
        })();

        // Release the signature if the effect is torn down before the async run finishes
        // (e.g. React 18 StrictMode dev double-invoke) so the remount can retry. The empty-only
        // merge in computePrefillUpdates keeps a second application idempotent.
        return () => {
            cancelled = true;
            if (!completed) ranSignatureRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on stable `signature`; refs hold latest formData/onChange/options.
    }, [signature, enabled, refFields]);
}

/** Rich fields (`list`, `tabs`, `iframe`, `repeatable`, `inspectionItems`) default to a full row unless `width` says otherwise. */
const isPanelField = (fd: FieldDef): boolean =>
    fd.type === 'list' || fd.type === 'tabs' || fd.type === 'iframe' || fd.type === 'repeatable'
    || fd.type === 'inspectionItems';

/** Two columns once the form is at least this wide; three once it reaches the wide cutoff. */
const FORM_TWO_COLUMN_MIN = 500;
const FORM_THREE_COLUMN_MIN = 1064;

type FormLayoutTier = 'compact' | 'medium' | 'wide';

function tierForFormWidth(width: number): FormLayoutTier {
    if (width >= FORM_THREE_COLUMN_MIN) return 'wide';
    if (width >= FORM_TWO_COLUMN_MIN) return 'medium';
    return 'compact';
}

/**
 * Tracks the width of the form itself. A narrow chat strip stacks fields; widening the window
 * or opening full screen places them side by side. Measuring the form (not the monitor) keeps
 * a small composer from squeezing three fields onto one line.
 */
function useFormLayoutTier() {
    const ref = useRef<HTMLDivElement>(null);
    const [tier, setTier] = useState<FormLayoutTier>('compact');
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const apply = (width: number) => {
            const next = tierForFormWidth(width);
            setTier((current) => (current === next ? current : next));
        };
        apply(el.clientWidth);
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver((entries) => {
            apply(entries[0]?.contentRect.width ?? 0);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return [ref, tier] as const;
}

/** Static class names so Tailwind can see every span. */
const COLUMN_SPAN_CLASS: Record<number, string> = {
    1: 'col-span-1',
    2: 'col-span-2',
    3: 'col-span-3',
    4: 'col-span-4',
    5: 'col-span-5',
    6: 'col-span-6',
    7: 'col-span-7',
    8: 'col-span-8',
    9: 'col-span-9',
    10: 'col-span-10',
    11: 'col-span-11',
    12: 'col-span-12',
};

const WIDTH_ALIASES: Record<string, number> = {
    full: 12,
    '100%': 12,
    half: 6,
    '50%': 6,
    third: 4,
    '33%': 4,
    '33.33%': 4,
    quarter: 3,
    '25%': 3,
    'two-thirds': 8,
    '66%': 8,
    '67%': 8,
    'three-quarters': 9,
    '75%': 9,
};

function clampColumnSpan(span: number): number {
    return Math.min(12, Math.max(1, Math.round(span)));
}

/** Column span (out of 12) requested by `width`, or null when the field should use the default. */
function configuredColumnSpan(width: string | number | undefined): number | null {
    if (width == null || width === '') return null;
    if (typeof width === 'number' && Number.isFinite(width)) {
        if (width > 12 && width <= 100) return clampColumnSpan((width / 100) * 12);
        return clampColumnSpan(width);
    }
    const token = String(width).trim().toLowerCase();
    if (token === 'auto') return null;
    if (token in WIDTH_ALIASES) return WIDTH_ALIASES[token];
    const percent = /^(\d+(?:\.\d+)?)%$/.exec(token);
    if (percent) return clampColumnSpan((Number(percent[1]) / 100) * 12);
    const columns = /^(\d+)\s*\/\s*12$/.exec(token);
    if (columns) return clampColumnSpan(Number(columns[1]));
    const asNumber = Number(token);
    if (token !== '' && Number.isFinite(asNumber)) return clampColumnSpan(asNumber);
    return null;
}

/** How many of the 12 columns this field occupies at the current form width. */
function spanForTier(fd: FieldDef, tier: FormLayoutTier): number {
    const configured = configuredColumnSpan(fd.width);
    if (tier === 'compact') return 12;
    if (configured == null) {
        if (isPanelField(fd)) return 12;
        return tier === 'wide' ? 4 : 6;
    }
    if (configured >= 12) return 12;
    // Below the wide cutoff, don't shrink a field under half a row.
    if (tier === 'medium') return Math.min(12, Math.max(configured, 6));
    return configured;
}

type LaidOutSlot = { slot: FieldDef[]; span: number };

/**
 * Packs slots into rows in json order. A field that does not fit the space left on the current
 * row starts the next row, so a full-width field (the inspection list) stays where it is declared
 * instead of being dropped below the fields that follow it. One shared CSS grid will do that:
 * auto-placement fills earlier gaps with later, narrower fields.
 */
function packFieldRows(
    slots: FieldDef[][],
    formData: Record<string, string>,
    tier: FormLayoutTier,
): LaidOutSlot[][] {
    const rows: LaidOutSlot[][] = [];
    let current: LaidOutSlot[] = [];
    let used = 0;
    for (const slot of slots) {
        const basis = slot.find((fd) => isVisibleField(fd, formData)) ?? slot[0];
        const span = Math.min(12, spanForTier(basis, tier));
        if (current.length > 0 && used + span > 12) {
            rows.push(current);
            current = [];
            used = 0;
        }
        current.push({ slot, span });
        used += span;
        if (used >= 12) {
            rows.push(current);
            current = [];
            used = 0;
        }
    }
    if (current.length > 0) rows.push(current);
    return rows;
}

/**
 * Groups fields into layout slots in json order.
 *
 * Slots cover every field that *could* appear (ignoring `visibleWhen`); slots with nothing visible
 * are dropped when the form is laid out. Fields
 * sharing the same `visibleWhen.field` are mutually exclusive alternatives (e.g. Phone Number vs.
 * Email Address, both gated on Send Via) and share one slot, so whichever is shown takes the
 * other's place instead of pushing onto a new column. A field gated with `in` can be shown
 * alongside its siblings (e.g. both Phone and Email for "Both"), so it keeps a slot of its own.
 */
function buildFieldSlots(fieldDefs: FieldDef[]): FieldDef[][] {
    const slots: FieldDef[][] = [];
    const slotIndexByVisibleWhenField = new Map<string, number>();
    for (const fd of fieldDefs) {
        const groupKey = isPanelField(fd) || fd.visibleWhen?.in ? undefined : fd.visibleWhen?.field;
        const existingIndex = groupKey ? slotIndexByVisibleWhenField.get(groupKey) : undefined;
        if (existingIndex !== undefined) {
            slots[existingIndex].push(fd);
            continue;
        }
        const index = slots.push([fd]) - 1;
        if (groupKey) slotIndexByVisibleWhenField.set(groupKey, index);
    }
    return slots;
}

type CommandRequestExecutor = (
    args: { endpoint: string; method?: string; body?: unknown },
) => { unwrap: () => Promise<any> };

export type CommandStepRunners = {
    executeRequest: CommandRequestExecutor;
    /** Runs the web agent with this code and resolves with its final result. */
    runWebAgent: (agentCode: string, inputs: Record<string, unknown>) => Promise<unknown>;
    /** Runs the integration action with this code and resolves with its response. */
    runIntegration: (args: { code: string; payload: unknown; endpoint?: string; method?: string }) => Promise<unknown>;
};

/** An external service is called without the user's session: only the json's own headers are sent. */
async function fetchExternal(url: string, step: CommandRequestStep, context: Record<string, any>): Promise<unknown> {
    const body = resolveConfiguredValue(step.body, context);
    const response = await fetch(url, {
        method: step.method ?? 'GET',
        headers: resolveConfiguredValue(step.headers ?? {}, context) as Record<string, string>,
        credentials: 'omit',
        ...(step.body === undefined ? {} : { body: typeof body === 'string' ? body : JSON.stringify(body) }),
    });
    if (!response.ok) throw new Error(`Request to ${url} failed: ${response.status}`);
    return response.json();
}

async function runCommandStep(
    step: CommandRequestStep,
    context: Record<string, any>,
    runners: CommandStepRunners,
): Promise<unknown> {
    if (step.kind === 'webAgent') {
        const inputs = resolveConfiguredValue(step.inputs ?? {}, context) as Record<string, unknown>;
        return runners.runWebAgent(step.agentCode ?? '', inputs);
    }
    if (step.kind === 'integration') {
        if (!step.code) throw new Error(`Missing integration code for ${step.key}`);
        const integrationEndpoint = resolveConfiguredValue(step.endpoint, context);
        // Same rule as `request`: only this application's API receives the user's session.
        if (integrationEndpoint !== undefined && (typeof integrationEndpoint !== 'string' || !integrationEndpoint.startsWith('/'))) {
            throw new Error(`Unable to resolve endpoint for ${step.key}`);
        }
        return runners.runIntegration({
            code: step.code,
            payload: resolveConfiguredValue(step.payload ?? {}, context),
            endpoint: integrationEndpoint,
            method: step.method,
        });
    }
    const endpoint = resolveConfiguredValue(step.endpoint, context);
    if (typeof endpoint === 'string' && endpoint.startsWith('https://')) return fetchExternal(endpoint, step, context);
    // Only this application's API base receives the user's session. This prevents a remotely
    // configured form from forwarding the user's bearer token off-site.
    if (typeof endpoint !== 'string' || !endpoint.startsWith('/')) {
        throw new Error(`Unable to resolve endpoint for ${step.key}`);
    }
    const body = resolveConfiguredValue(step.body, context);
    return runners.executeRequest({
        endpoint,
        method: step.method,
        ...(step.body === undefined ? {} : { body }),
    }).unwrap();
}

/** Runs request steps in order; each step's result is exposed to later steps under its `key`. */
export async function runCommandSteps(
    steps: CommandRequestStep[],
    formData: Record<string, string>,
    runners: CommandStepRunners,
): Promise<Record<string, any>> {
    // Each step reads the previous response, so they stay sequential. `reduce` keeps that
    // order without an `await` inside a loop.
    return steps.reduce<Promise<Record<string, any>>>(async (pending, step) => {
        const current = await pending;
        const response = await runCommandStep(step, current, runners);
        const result = step.select
            ? Object.fromEntries(Object.entries(step.select).map(([alias, path]) => [alias, readByPath(response as any, path)]))
            : readByPath(response as any, step.responsePath);
        return { ...current, [step.key]: result };
    }, Promise.resolve({ form: formData }));
}

/** Runs a configured field's request steps in order and resolves its iframe URL. */
async function resolveCommandIframeUrl(
    config: CommandIframeConfig,
    formData: Record<string, string>,
    runners: CommandStepRunners,
): Promise<string> {
    const context = await runCommandSteps(config.steps, formData, runners);
    const url = resolveConfiguredValue(config.url, context);
    if (typeof url !== 'string' || !url) throw new Error('Unable to resolve iframe URL');
    return url;
}

/** Request and web agent runners bound to the signed-in user, shared by every request chain. */
function useCommandStepRunners(): CommandStepRunners {
    const [executeRequest] = useExecuteCommandRequestMutation();
    const [executeIntegration] = useExecuteCommandIntegrationMutation();
    const dispatch = useDispatch();
    const token = useSelector((state: any) => state?.auth?.token ?? null) as string | null;
    const tokenRef = useRef(token);
    tokenRef.current = token;
    return useMemo(() => ({
        executeRequest,
        runWebAgent: async (agentCode, inputs) => runWebAgent({
            spec: await loadWebAgentSpecByCode(dispatch as any, agentCode),
            inputs,
            token: tokenRef.current,
        }),
        runIntegration: (args) => executeIntegration(args).unwrap(),
    }), [executeRequest, executeIntegration, dispatch]);
}

const FORM_PLACEHOLDER_PATTERN = /\{\{form\.([^{}"]+)\}\}/gu;

/** Identifies one resolved URL: the configuration with every form value it interpolates
 *  substituted in, so a change to any of them is a new run. Empty while a declared `dependsOn`
 *  field is still blank — there is nothing to resolve yet. */
function buildIframeSignature(config: Pick<CommandIframeConfig, 'dependsOn'>, formData: Record<string, string>): string {
    if (!(config.dependsOn ?? []).every((key) => formData[key]?.trim())) return '';
    return JSON.stringify(config)
        .replace(FORM_PLACEHOLDER_PATTERN, (_match, key: string) => formData[key.trim()] ?? '');
}

/** What each `iframe` field is showing: its resolved URL, or that it is still resolving or failed. */
export type CommandIframeState = Record<string, { url?: string; loading?: boolean; error?: boolean }>;

/**
 * Resolves the iframe URL for every configured `iframe` field by running its request steps.
 *
 * Owned by the form itself rather than by the field, so that collapsing the form or expanding it
 * to full screen reuses the URL already resolved instead of repeating the requests.
 *
 * A chain runs again only when its signature changes, i.e. when the configuration or one of the
 * form values it interpolates changes. The signature a chain started under is remembered across
 * effect re-runs (including React's dev double-invoke), so the same chain is never started twice.
 */
function useCommandIframeSources(
    commandJson: Record<string, any> | undefined,
    formData: Record<string, string>,
    enabled: boolean,
): CommandIframeState {
    const [state, setState] = useState<CommandIframeState>({});
    const runners = useCommandStepRunners();

    const fieldDefs = useMemo(() => parseFieldDefs(commandJson), [commandJson]);
    const runs = useMemo(
        () => fieldDefs
            .filter((fd) => fd.type === 'iframe' && fd.iframe?.steps?.length && fd.iframe?.url)
            .map((fd) => ({
                key: fd.key,
                config: fd.iframe as CommandIframeConfig,
                signature: buildIframeSignature(fd.iframe as CommandIframeConfig, formData),
            })),
        [fieldDefs, formData],
    );
    const runsSignature = runs.map((run) => `${run.key}\u0000${run.signature}`).join('|');

    const latest = useRef({ runs, formData });
    latest.current = { runs, formData };
    // The signature each field's chain was last started under; never cleared on effect cleanup,
    // so a re-run of this effect resumes the chain already in flight instead of starting another.
    const startedSignatureRef = useRef<Record<string, string>>({});

    useEffect(() => {
        if (!enabled) return;

        for (const { key, config, signature } of latest.current.runs) {
            if (startedSignatureRef.current[key] === signature) continue;
            startedSignatureRef.current[key] = signature;
            const setField = (field: CommandIframeState[string]) => {
                if (startedSignatureRef.current[key] === signature) {
                    setState((prev) => ({ ...prev, [key]: field }));
                }
            };

            // Nothing to resolve yet: clear whatever the field was showing.
            if (!signature) {
                setField({});
                continue;
            }

            setField({ loading: true });
            resolveCommandIframeUrl(config, latest.current.formData, runners)
                .then((url) => setField({ url }))
                .catch(() => {
                    setField({ error: true });
                    // Leave the field retryable rather than stuck on this failure.
                    startedSignatureRef.current[key] = '';
                });
        }
        // Keyed on `runsSignature`; the configs and form values themselves are read from a ref.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runsSignature, enabled, runners]);

    return state;
}

/** The `sor` query with its `{{form.<field>}}` placeholders filled in, as JSON. Empty while a
 *  placeholder's field is still blank — there is nothing to query yet. */
function buildSorQuerySignature(source: FieldSource, formData: Record<string, string>): string {
    let missing = false;
    const signature = JSON.stringify([source.definition, source.filter ?? null])
        .replace(FORM_PLACEHOLDER_PATTERN, (_match, key: string) => {
            const value = formData[key.trim()]?.trim() ?? '';
            if (!value) missing = true;
            // Escaped, since the value lands inside a JSON string
            return JSON.stringify(value).slice(1, -1);
        });
    return missing ? '' : signature;
}

/** What each `inspectionItems` field is waiting on. */
export type CommandInspectionItemsState = Record<string, { loading?: boolean; error?: boolean }>;

const FAILED_SIGNATURE = '\u0000failed';

/**
 * Loads every `inspectionItems` field's items from its `sor` source into the form, e.g. the
 * checklist of the selected Inspection Template. A new query (the template changed) replaces the
 * items, including any the user added; a blank dependency clears them. Items already in the form
 * on the first run (a rerun or a restored draft) are kept rather than reloaded.
 *
 * Owned by the form rather than by the field, so collapsing the form keeps the user's edits.
 */
function useCommandInspectionItemsSources(
    fieldDefs: FieldDef[],
    formData: Record<string, string>,
    emitFormData: (next: Record<string, string>) => void,
    enabled: boolean,
): { state: CommandInspectionItemsState; retry: () => void } {
    const [state, setState] = useState<CommandInspectionItemsState>({});
    const [retryCount, setRetryCount] = useState(0);
    const retry = useCallback(() => setRetryCount((count) => count + 1), []);
    const { queryRecords } = useSorApi();

    const runs = useMemo(
        () => fieldDefs
            .filter((fd) => fd.type === 'inspectionItems' && fd.source && isSorSource(fd.source))
            .map((fd) => ({ key: fd.key, signature: buildSorQuerySignature(fd.source as FieldSource, formData) })),
        [fieldDefs, formData],
    );
    const runsSignature = runs.map((run) => `${run.key}\u0000${run.signature}`).join('|');

    const latest = useRef({ runs, formData, emitFormData, queryRecords });
    latest.current = { runs, formData, emitFormData, queryRecords };
    // The query each field's items were last loaded (or kept) for.
    const appliedRef = useRef<Record<string, string>>({});
    // A load finishing after the form is gone must not write into the host's next draft.
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        if (!enabled) return;

        for (const { key, signature } of latest.current.runs) {
            const previous = appliedRef.current[key];
            if (previous === signature) continue;

            const stored = latest.current.formData[key] ?? '';
            // Items already in the form before any load (a rerun or a restored draft) are kept. When
            // the template they belong to isn't filled in yet, they're kept until it is, rather than
            // being cleared and then reloaded over the user's edits.
            if (previous === undefined && stored.trim()) {
                if (signature) appliedRef.current[key] = signature;
                continue;
            }

            appliedRef.current[key] = signature;
            const isCurrent = () => mountedRef.current && appliedRef.current[key] === signature;
            const setField = (field: CommandInspectionItemsState[string]) => {
                if (isCurrent()) setState((prev) => ({ ...prev, [key]: field }));
            };
            // The items belong to the previous selection, so they go before the new ones load.
            if (stored) latest.current.emitFormData({ ...latest.current.formData, [key]: '' });
            if (!signature) {
                setField({});
                continue;
            }

            setField({ loading: true });
            const [definition, filter] = JSON.parse(signature) as [string, SorFilter | null];
            loadSorSourceRecords(latest.current.queryRecords, definition, filter)
                .then((records) => {
                    if (!isCurrent()) return;
                    latest.current.emitFormData({
                        ...latest.current.formData,
                        [key]: serializeInspectionItems(inspectionItemsFromRecords(records)),
                    });
                    setField({});
                })
                .catch(() => {
                    setField({ error: true });
                    // Loaded again on `retry` or the next change to the form's queries.
                    if (isCurrent()) appliedRef.current[key] = FAILED_SIGNATURE;
                });
        }
        // Keyed on `runsSignature`; the form values and callbacks are read from a ref.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runsSignature, enabled, retryCount]);

    return { state, retry };
}

/*
 * The named `prefill.match` finders below scan text in one pass rather than with a single regex,
 * whose backtracking would make long concern text slow. They return exactly what these patterns
 * would:
 *   email: [\w.%+-]+@[\w-]+(?:\.[\w-]+)*\.(?:[a-z]{2,}(?![a-z])|[A-Z]{2,}(?![A-Za-z]))
 *   phone: (?<![\d+])(?:\+?\d{1,3}[\s.-]?)?(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}(?!\d)
 */
const isWordChar = (char: string | undefined): boolean => char !== undefined && /^\w$/u.test(char);
const isLabelChar = (char: string | undefined): boolean => isWordChar(char) || char === '-';
const isEmailLocalChar = (char: string | undefined): boolean => isWordChar(char) || (char !== undefined && '.%+-'.includes(char));
const isLowerChar = (char: string | undefined): boolean => char !== undefined && char >= 'a' && char <= 'z';
const isUpperChar = (char: string | undefined): boolean => char !== undefined && char >= 'A' && char <= 'Z';
const isDigitChar = (char: string | undefined): boolean => char !== undefined && char >= '0' && char <= '9';
const isPhoneSeparator = (char: string | undefined): boolean => char !== undefined && /^[\s.-]$/u.test(char);

function runLength(text: string, start: number, test: (char: string) => boolean): number {
    let end = start;
    while (end < text.length && test(text[end])) end += 1;
    return end - start;
}

/** Length of the top-level domain starting here. It may not run straight on into lowercase text,
 *  so "…@site.comPhotos" ends at ".com". */
function topLevelDomainLength(text: string, start: number): number {
    const lower = runLength(text, start, isLowerChar);
    if (lower >= 2) return lower;
    const upper = runLength(text, start, isUpperChar);
    return upper >= 2 && !isLowerChar(text[start + upper]) ? upper : 0;
}

/** Where an email's domain starting here ends: at the last dotted label that opens with a valid
 *  top-level domain. -1 when there is none. */
function emailDomainEnd(text: string, start: number): number {
    const first = runLength(text, start, isLabelChar);
    if (!first) return -1;
    const labelStarts: number[] = [];
    let position = start + first;
    while (text[position] === '.' && isLabelChar(text[position + 1])) {
        labelStarts.push(position + 1);
        position += 1 + runLength(text, position + 1, isLabelChar);
    }
    for (let k = labelStarts.length - 1; k >= 0; k -= 1) {
        const length = topLevelDomainLength(text, labelStarts[k]);
        if (length) return labelStarts[k] + length;
    }
    return -1;
}

function findEmail(text: string): string | undefined {
    for (let at = text.indexOf('@'); at >= 0; at = text.indexOf('@', at + 1)) {
        let start = at;
        while (start > 0 && isEmailLocalChar(text[start - 1])) start -= 1;
        const end = start < at ? emailDomainEnd(text, at + 1) : -1;
        if (end >= 0) return text.slice(start, end);
    }
    return undefined;
}

/** The number after an optional country code, tried at one position at a time (sticky). */
const PHONE_BODY_PATTERN = /(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}(?!\d)/uy;

/** Where the number may start after an optional `+` and 1–3 digit country code at `start`, most
 *  preferred first: longest code, with its separator, down to no code at all. */
function phoneBodyOffsets(text: string, start: number): number[] {
    const offsets: number[] = [];
    const digitsStart = start + (text[start] === '+' ? 1 : 0);
    for (let digits = Math.min(3, runLength(text, digitsStart, isDigitChar)); digits >= 1; digits -= 1) {
        const end = digitsStart + digits;
        if (isPhoneSeparator(text[end])) offsets.push(end + 1);
        offsets.push(end);
    }
    offsets.push(start);
    return offsets;
}

function findPhone(text: string): string | undefined {
    for (let start = 0; start < text.length; start += 1) {
        if (isDigitChar(text[start - 1]) || text[start - 1] === '+') continue;
        for (const offset of phoneBodyOffsets(text, start)) {
            PHONE_BODY_PATTERN.lastIndex = offset;
            const body = PHONE_BODY_PATTERN.exec(text);
            if (body) return text.slice(start, offset + body[0].length);
        }
    }
    return undefined;
}

/** Named finders a `prefill.match` can use; any other text shape can use a custom `pattern`. */
const PREFILL_FINDERS: Record<string, (text: string) => string | undefined> = {
    email: findEmail,
    phone: findPhone,
};

function prefillFinder(prefill: FieldPrefill): ((text: string) => string | undefined) | undefined {
    if (prefill.match) return PREFILL_FINDERS[prefill.match];
    if (!prefill.pattern) return undefined;
    try {
        const pattern = new RegExp(prefill.pattern, 'u');
        return (text) => pattern.exec(text)?.[0];
    } catch {
        return undefined;
    }
}

/** A field's value from a finished chain: the value at `prefill.path`, narrowed to the first
 *  `match`/`pattern` found in its text (objects and arrays are searched as json). */
export function extractPrefillValue(context: Record<string, any>, prefill: FieldPrefill): string {
    const value = readByPath(context, prefill.path);
    if (!prefill.match && !prefill.pattern) return value == null || typeof value === 'object' ? '' : String(value).trim();
    const text = typeof value === 'string' ? value : JSON.stringify(value ?? '');
    return prefillFinder(prefill)?.(text)?.trim() ?? '';
}

/** Values a finished chain writes: only into fields that are empty or still hold the value written
 *  there last (`written`), so a user's own edit is never overwritten. */
function collectPrefillUpdates(
    fieldDefs: FieldDef[],
    sourceName: string,
    context: Record<string, any>,
    formData: Record<string, string>,
    written: Record<string, string>,
): Record<string, string> {
    const updates: Record<string, string> = {};
    for (const fd of fieldDefs) {
        if (fd.prefill?.source !== sourceName) continue;
        const value = extractPrefillValue(context, fd.prefill);
        const existing = (formData[fd.key] ?? '').trim();
        if (value && value !== existing && (!existing || existing === written[fd.key])) updates[fd.key] = value;
    }
    return updates;
}

/**
 * Runs each `$sources` chain a field's `prefill` names, the same way `useCommandIframeSources` runs
 * an iframe's chain (once per signature; set `dependsOn` so it waits for its inputs), and writes
 * the extracted values into the form. Returns which sources are still running.
 */
function useCommandSourcePrefill(
    commandJson: Record<string, any> | undefined,
    fieldDefs: FieldDef[],
    formData: Record<string, string>,
    onFormDataChange: ((data: Record<string, string>) => void) | undefined,
    enabled: boolean,
): Record<string, boolean> {
    const { t } = useTranslation();
    const runners = useCommandStepRunners();
    const [loading, setLoading] = useState<Record<string, boolean>>({});

    const runs = useMemo(() => {
        const sources = optionalObject<Record<string, Pick<CommandIframeConfig, 'dependsOn' | 'steps'>>>(commandJson?.$sources) ?? {};
        const used = new Set(fieldDefs.map((fd) => fd.prefill?.source));
        return Object.entries(sources)
            .filter(([name, config]) => used.has(name) && Array.isArray(config?.steps))
            .map(([name, config]) => ({ name, config, signature: buildIframeSignature(config, formData) }));
    }, [commandJson, fieldDefs, formData]);
    const runsSignature = runs.map((run) => `${run.name}\u0000${run.signature}`).join('|');

    const latest = useRef({ runs, fieldDefs, formData, onFormDataChange });
    latest.current = { runs, fieldDefs, formData, onFormDataChange };
    const startedSignatureRef = useRef<Record<string, string>>({});
    const writtenRef = useRef<Record<string, string>>({});

    useEffect(() => {
        if (!enabled) return;

        for (const { name, config, signature } of latest.current.runs) {
            if (startedSignatureRef.current[name] === signature) continue;
            startedSignatureRef.current[name] = signature;
            const isCurrent = () => startedSignatureRef.current[name] === signature;
            setLoading((prev) => ({ ...prev, [name]: Boolean(signature) }));
            if (!signature) continue;

            runCommandSteps(config.steps, latest.current.formData, runners)
                .then((context) => {
                    if (!isCurrent()) return;
                    const { fieldDefs: defs, formData: current, onFormDataChange: onChange } = latest.current;
                    const updates = collectPrefillUpdates(defs, name, context, current, writtenRef.current);
                    if (Object.keys(updates).length === 0) return;
                    Object.assign(writtenRef.current, updates);
                    onChange?.({ ...current, ...updates });
                })
                .catch((error: unknown) => {
                    if (!isCurrent()) return;
                    // A failed chain is retried only when its inputs change, so a slow web agent never loops.
                    console.error(`[command form] Source "${name}" failed`, error);
                    const reason = error instanceof Error && error.message ? `: ${error.message}` : '';
                    CtyShowToast(checkType.error, `${t('Failed to load form details')}${reason}`);
                })
                .finally(() => {
                    if (isCurrent()) setLoading((prev) => ({ ...prev, [name]: false }));
                });
        }
        // Keyed on `runsSignature`; configs, fields and form values are read from a ref.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runsSignature, enabled, runners]);

    return loading;
}

interface CommandFormFieldsProps {
    commandJson: Record<string, any>;
    formData: Record<string, string>;
    customMessage: string;
    onFieldChange: (key: string, value: string) => void;
    onCustomMessageChange: (msg: string) => void;
    dropdownOptions: Record<string, DropdownOption[]>;
    dropdownLoading: Record<string, boolean>;
    dropdownRecords: Record<string, Record<string, any>[]>;
    /** Iframe URLs already resolved by the owning form, so this renderer never fetches its own. */
    iframeState: CommandIframeState;
    /** `inspectionItems` fields still loading or failed; their items are in `formData`. */
    inspectionItemsState?: CommandInspectionItemsState;
    onRetryInspectionItems?: () => void;
    /** `$sources` chains still running, by source name. */
    prefillLoading?: Record<string, boolean>;
    readOnly?: boolean;
    compact?: boolean;
    showErrors?: boolean;
    onEnterPress?: () => void;
    /** Repeatable field key → the repeatable field whose item count it follows. */
    repeatableFollows?: Record<string, string>;
    onUploadingChange?: (busy: boolean) => void;
    /** Web agent steps. An attachment field reads accept, maxSizeMb, and multiple from its uploadFile step. */
    steps?: ReadonlyArray<WebAgentStep>;
}

/** Pure field renderer: everything it shows is resolved and owned by `CollapsibleCommandForm`. */
function CommandFormFields({
    commandJson,
    formData,
    customMessage,
    onFieldChange,
    onCustomMessageChange,
    dropdownOptions,
    dropdownLoading,
    dropdownRecords,
    iframeState,
    inspectionItemsState = {},
    onRetryInspectionItems,
    prefillLoading,
    readOnly = false,
    compact = true,
    showErrors = false,
    onEnterPress,
    repeatableFollows: followsMap = {},
    onUploadingChange,
    steps,
}: Readonly<CommandFormFieldsProps>) {
    const { t } = useTranslation();
    const [getPresignedUrl] = useGetAiworkersPresignedUrlMutation();
    const [uploadFileToS3] = useUploadFileToS3Mutation();
    const [uploadingKey, setUploadingKey] = useState<string | null>(null);
    const [uploadsByField, setUploadsByField] = useState<Record<string, LocalAttachment[]>>({});
    useEffect(() => {
        onUploadingChange?.(uploadingKey != null);
        return () => onUploadingChange?.(false);
    }, [onUploadingChange, uploadingKey]);
    useEffect(() => {
        setUploadsByField((prev) => {
            let changed = false;
            const next: Record<string, LocalAttachment[]> = {};
            // A photo on a repeatable row is stored inside that row's JSON, not under its own form key.
            const nestedKeys = new Set<string>();
            for (const value of Object.values(formData)) {
                let parsed: unknown;
                try { parsed = JSON.parse(value); } catch { continue; }
                if (!Array.isArray(parsed)) continue;
                for (const item of parsed) {
                    if (!item || typeof item !== 'object') continue;
                    for (const field of Object.values(item as Record<string, unknown>)) {
                        if (typeof field === 'string') readStoredAttachments(field).forEach((file) => nestedKeys.add(file.s3_key));
                    }
                }
            }
            for (const [key, items] of Object.entries(prev)) {
                const owned = formData[key] !== undefined;
                const kept = new Set(owned ? readStoredAttachments(formData[key]).map((file) => file.s3_key) : nestedKeys);
                const filtered = items.filter((item) => item.uploading || (item.s3_key != null && kept.has(item.s3_key)));
                next[key] = filtered;
                if (filtered.length !== items.length) changed = true;
            }
            return changed ? next : prev;
        });
    }, [formData]);
    const [formLayoutRef, layoutTier] = useFormLayoutTier();
    const fieldDefs = useMemo(() => parseFieldDefs(commandJson), [commandJson]);
    // Which `list` field (by key) is currently showing its "pick a type" add-item chooser.
    const [addingListKey, setAddingListKey] = useState<string | null>(null);

    // The "Custom" option + free-text message coupling is a legacy behavior of the
    // `configuration-actions` action-picker dropdown only. A generic templated/localStorage/api
    // dropdown (e.g. Inspection Template) is a plain value picker and must not get an unwanted
    // "Custom" entry injected or force the bottom message textarea to appear.
    const hasCustomSelected = fieldDefs.some(
        (fd) => isLegacyActionDropdown(fd) && formData[fd.key] === CUSTOM_DROPDOWN_KEY,
    );

    // A non-custom dropdown option is selected whose message text is stored in `customMessage`.
    const hasOptionMessage = !hasCustomSelected && !!customMessage?.trim() && fieldDefs.some(
        (fd) => isLegacyActionDropdown(fd) && formData[fd.key] && formData[fd.key] !== CUSTOM_DROPDOWN_KEY,
    );

    const renderLabel = (fd: FieldDef) => (
        <label htmlFor={fd.key} className="text-xs font-medium text-[#414651] font-[Poppins]">
            {fieldLabel(fd)}
            {isRequiredField(fd, formData) && <span className="text-danger ml-0.5">*</span>}
        </label>
    );

    const submitOnEnter = (e: React.KeyboardEvent) => {
        if (e.key !== 'Enter' || e.shiftKey) return;
        e.preventDefault();
        onEnterPress?.();
    };

    const isPrefillingField = (fd: FieldDef): boolean =>
        !readOnly && Boolean(fd.prefill && prefillLoading?.[fd.prefill.source]);

    const renderTextInput = (fd: FieldDef) => {
        const val = formData[fd.key] ?? '';
        const isEmpty = showErrors && !readOnly && isRequiredField(fd, formData) && !val.trim();
        const label = fieldLabel(fd).toLowerCase();
        const sharedProps = {
            id: fd.key,
            size: compact ? 'sm' as const : 'md' as const,
            'aria-label': fd.key,
            value: val,
            placeholder: `${t('Enter')} ${label}`,
            onValueChange: (v: string) => onFieldChange(fd.key, v),
            isReadOnly: readOnly,
            isInvalid: isEmpty,
            errorMessage: isEmpty ? `${t('Please enter')} ${label}` : undefined,
            endContent: isPrefillingField(fd) ? <Spinner size="sm" /> : undefined,
        };
        return (
            <div className="flex flex-col gap-1 min-w-0" key={fd.key}>
                {renderLabel(fd)}
                {/* A multiline field keeps Enter for newlines rather than submitting the form. */}
                {fd.multiline ? (
                    <Textarea
                        {...sharedProps}
                        minRows={compact ? 2 : 3}
                        maxRows={compact ? 4 : 6}
                        classNames={{ input: 'overflow-y-auto' }}
                    />
                ) : (
                    <Input
                        {...sharedProps}
                        type="text"
                        inputMode={fd.type === 'number' ? 'numeric' : undefined}
                        onKeyDown={submitOnEnter}
                    />
                )}
            </div>
        );
    };

    const renderReadOnlyDropdown = (fd: FieldDef, selectedValue: string) => {
        const displayLabel = selectedValue === CUSTOM_DROPDOWN_KEY ? t('Custom') : buildFieldLabel(selectedValue);
        const readOnlyItems = selectedValue ? [{ key: selectedValue, label: displayLabel }] : [];
        return (
            <div className="flex flex-col gap-1 min-w-0" key={fd.key}>
                {renderLabel(fd)}
                <Autocomplete
                    id={fd.key}
                    size={compact ? 'sm' : 'md'}
                    aria-label={fd.key}
                    selectedKey={selectedValue || null}
                    isDisabled
                    defaultItems={readOnlyItems}
                >
                    {(opt) => <AutocompleteItem key={opt.key}>{opt.label}</AutocompleteItem>}
                </Autocomplete>
            </div>
        );
    };

    /** Stores the selection; a legacy action dropdown also fills the message with the action's
     *  value, or clears it for Custom. */
    const handleDropdownSelection = (fd: FieldDef, key: React.Key | null) => {
        const val = key == null ? '' : fromCommandOptionKey(String(key));
        onFieldChange(fd.key, val);
        if (!isLegacyActionDropdown(fd)) return;
        const opt = val === CUSTOM_DROPDOWN_KEY ? undefined : (dropdownOptions[fd.key] ?? []).find((o) => o.key === val);
        onCustomMessageChange(opt?.value ?? '');
    };

    const renderDropdown = (fd: FieldDef) => {
        const selectedValue = formData[fd.key] ?? '';
        if (readOnly) return renderReadOnlyDropdown(fd, selectedValue);
        const isEmpty = showErrors && isRequiredField(fd, formData) && !selectedValue.trim();

        const isLoading = dropdownLoading[fd.key] ?? false;
        const isLegacyAction = isLegacyActionDropdown(fd);
        const dependsOnField = fd.source?.when?.field;
        const isBlocked = Boolean(dependsOnField && !(formData[dependsOnField] ?? '').trim());
        const baseOptions = dropdownChoices(fd, formData, dropdownOptions, dropdownRecords);
        const allOptions = isLegacyAction ? [...baseOptions, { key: CUSTOM_DROPDOWN_KEY, label: t('Custom') }] : baseOptions;
        const showFieldError = isEmpty && !isBlocked;

        return (
            <div className="flex flex-col gap-1 min-w-0" key={fd.key}>
                {renderLabel(fd)}
                {isLoading && !isBlocked ? (
                    <div className="flex items-center gap-2 py-1">
                        <Spinner size="sm" />
                        <span className="text-xs text-default-500">{t('Loading options...')}</span>
                    </div>
                ) : (
                    <Autocomplete
                        key={dependsOnField ? (formData[dependsOnField] ?? '') : undefined}
                        id={fd.key}
                        placeholder={`${t('Select')} ${fieldLabel(fd).toLowerCase()}`}
                        size={compact ? 'sm' : 'md'}
                        aria-label={fd.key}
                        selectedKey={selectedValue ? toCommandOptionKey(selectedValue) : null}
                        isDisabled={isBlocked}
                        isClearable={allOptions.length !== 1}
                        onClear={() => onFieldChange(fd.key, '')}
                        onSelectionChange={(key) => handleDropdownSelection(fd, key)}
                        items={allOptions}
                        isInvalid={showFieldError}
                        errorMessage={showFieldError ? `${t('Please select')} ${fieldLabel(fd).toLowerCase()}` : undefined}
                        popoverProps={{ classNames: { content: 'w-auto min-w-[200px]' }, placement: 'bottom-start' }}
                        listboxProps={{ classNames: { list: 'max-w-none' } }}
                    >
                        {(opt) => (
                            <AutocompleteItem key={toCommandOptionKey(opt.key)} textValue={opt.label} classNames={{ base: 'max-w-none', title: 'whitespace-normal break-words' }}>
                                {opt.label}
                            </AutocompleteItem>
                        )}
                    </Autocomplete>
                )}
            </div>
        );
    };

    const renderDateInput = (fd: FieldDef) => {
        const val = formData[fd.key] ?? '';
        const isEmpty = showErrors && !readOnly && isRequiredField(fd, formData) && !val.trim();
        const ymd = toCommandDateYmd(val);
        let dateValue = null;
        if (ymd) {
            try { dateValue = parseDate(ymd); } catch { dateValue = null; }
        }
        return (
            <div className="flex flex-col gap-1 min-w-0" key={fd.key}>
                {renderLabel(fd)}
                <I18nProvider locale="en-US">
                    <Input
                        id={fd.key}
                        aria-label={fd.key}
                        type="text"
                        inputMode="numeric"
                        size={compact ? 'sm' : 'md'}
                        value={val}
                        placeholder="MM/DD/YYYY"
                        isReadOnly={readOnly}
                        isInvalid={isEmpty}
                        errorMessage={isEmpty ? `${t('Please enter')} ${fieldLabel(fd).toLowerCase()}` : undefined}
                        onValueChange={(v) => onFieldChange(fd.key, formatDateMask(v))}
                        onBlur={() => {
                            const normalized = toCommandDateMdy(val);
                            if (normalized !== val && toCommandDateYmd(normalized)) {
                                onFieldChange(fd.key, normalized);
                            }
                        }}
                        onKeyDown={(e) => {
                            // Block typing `/` — mask inserts it. Allow control/nav keys and digits.
                            if (e.key === '/' || e.key === '-') e.preventDefault();
                            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onEnterPress?.(); }
                        }}
                        endContent={
                            readOnly ? (
                                <Icon icon="mdi:calendar" className="h-4 w-4 text-default-400 shrink-0" />
                            ) : (
                                <Popover placement="bottom-end">
                                    <PopoverTrigger>
                                        <button
                                            type="button"
                                            aria-label={t('Open calendar')}
                                            className="flex items-center justify-center outline-none"
                                        >
                                            <Icon icon="mdi:calendar" className="h-4 w-4 text-default-400" />
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="p-2 bg-content1">
                                        <Calendar
                                            aria-label={fd.key}
                                            value={dateValue}
                                            onChange={(date) => {
                                                onFieldChange(fd.key, date ? toCommandDateMdy(date.toString()) : '');
                                            }}
                                        />
                                    </PopoverContent>
                                </Popover>
                            )
                        }
                        classNames={{
                            inputWrapper: 'bg-content1 border border-default-200',
                        }}
                    />
                </I18nProvider>
            </div>
        );
    };

    const renderRadio = (fd: FieldDef) => {
        const selectedValue = formData[fd.key] ?? '';
        const options = fd.options ?? (dropdownOptions[fd.key] ?? []).map((o) => ({ value: o.key, label: o.label }));
        const isEmpty = showErrors && !readOnly && isRequiredField(fd, formData) && !selectedValue.trim();
        return (
            <div className="flex flex-col gap-1 min-w-0" key={fd.key}>
                {renderLabel(fd)}
                <div className={`flex rounded-lg border ${isEmpty ? 'border-danger' : 'border-default-200'} p-0.5 gap-0.5`}>
                    {options.map((opt) => {
                        const isActive = selectedValue === opt.value;
                        return (
                            <button
                                type="button"
                                key={opt.value}
                                disabled={readOnly}
                                onClick={() => onFieldChange(fd.key, opt.value)}
                                className={`flex-1 rounded-md text-sm font-medium py-1.5 transition-colors ${
                                    isActive ? 'bg-primary text-white' : 'bg-transparent text-default-600'
                                }`}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
                {isEmpty && (
                    <span className="text-danger text-xs">{t('Please select')} {fieldLabel(fd).toLowerCase()}</span>
                )}
            </div>
        );
    };

    /** Resolves the array of checklist-item records a `list` field should render, whether that
     *  array comes from the field's own source or (via `ref`) from another field's selected record. */
    const resolveListItems = (fd: FieldDef): Record<string, any>[] => {
        if (fd.source?.kind === 'ref' && fd.source.field) {
            const records = dropdownRecords[fd.source.field] ?? [];
            const refFieldDef = fieldDefs.find((f) => f.key === fd.source?.field);
            const valueField = fd.source.valueField ?? refFieldDef?.source?.valueField ?? 'code';
            // Match against the same derived keys the referenced field's dropdown options use
            // (not the raw record field), so a duplicate/blank raw value can't resolve ambiguously.
            const keys = deriveOptionKeys(records, valueField);
            const idx = keys.indexOf(formData[fd.source.field] ?? '');
            const record = idx >= 0 ? records[idx] : undefined;
            const items = record ? readByPath(record, fd.source.path) : undefined;
            return Array.isArray(items) ? items : [];
        }
        return dropdownRecords[fd.key] ?? [];
    };

    /** A list the user adds and removes. Always shows at least one item. Stored as a JSON array of objects. */
    const renderRepeatableList = (fd: FieldDef) => {
        const fields = repeatableFields(fd) ?? {};
        const blank = () => Object.fromEntries(Object.keys(fields).map((key) => [key, '']));
        const stored = parseRepeatableItems(formData[fd.key]);
        // A list that follows another is resized by CollapsibleCommandForm; it has no Add/Remove of its own.
        const follows = Boolean(followsMap[fd.key]);
        const items = stored.length ? stored : [blank()];
        const write = (next: Record<string, string>[]) => onFieldChange(fd.key, JSON.stringify(next.length ? next : [blank()]));
        const setItem = (index: number, key: string, value: string) => {
            write(items.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
        };
        const control = (item: Record<string, string>, index: number, key: string, def: RepeatableField | undefined) => {
            const label = def?.label?.trim() || key;
            const value = String(item?.[key] ?? '');
            const required = def?.required !== false;
            if (def?.type === 'attachment' || uploadSteps(steps).some((step) => uploadStepNamesField(step, key))) {
                return renderAttachment(
                    { key: `${fd.key}-${index}-${key}`, type: 'attachment', label, required: def?.required },
                    { value, onChange: (next) => setItem(index, key, next), limitKey: key },
                );
            }
            if (def?.type === 'dropdown') {
                return (
                    <label key={key} className="flex flex-col gap-1 text-sm text-foreground">
                        <span>{label}{required ? ' *' : ''}</span>
                        <select
                            className="rounded-md border border-default-200 bg-content1 px-2 py-1.5 text-sm text-foreground"
                            disabled={readOnly}
                            value={value}
                            onChange={(e) => setItem(index, key, e.target.value)}
                        >
                            <option value="">{t('Select')}</option>
                            {(def.options ?? []).map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label || opt.value}</option>
                            ))}
                        </select>
                    </label>
                );
            }
            if (def?.multiline) {
                return (
                    <Textarea
                        key={key} size="sm" variant="bordered" labelPlacement="outside"
                        label={label} isRequired={required} isReadOnly={readOnly}
                        minRows={3} value={value}
                        onValueChange={(v) => setItem(index, key, v)}
                    />
                );
            }
            return (
                <Input
                    key={key} size="sm" variant="bordered" labelPlacement="outside"
                    label={label} isRequired={required} isReadOnly={readOnly}
                    value={value}
                    inputMode={def?.type === 'number' ? 'numeric' : undefined}
                    placeholder={def?.type === 'date' ? 'MM/DD/YYYY' : undefined}
                    onValueChange={(v) => setItem(index, key, v)}
                />
            );
        };
        // Same packing as top-level fields; a multiline field takes the whole row.
        const itemRows = packFieldRows(
            Object.entries(fields).map(([key, def]) => [{ key, type: 'string', width: def?.multiline || def?.type === 'attachment' ? 'full' : undefined } as FieldDef]),
            formData,
            layoutTier,
        );
        return (
            <div className="flex flex-col gap-2">
                {items.map((item, index) => (
                    <div key={`${fd.key}-${index}`} className="flex flex-col gap-2 rounded-lg border border-default-200 bg-content2 p-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-foreground">{index + 1}</span>
                            {!readOnly && !follows && index > 0 && (
                                <button
                                    type="button" aria-label={t('Remove')} title={t('Remove')}
                                    className="flex items-center gap-1 text-sm text-danger"
                                    onClick={() => write(items.filter((_, i) => i !== index))}
                                >
                                    <Icon icon="mdi:trash-can-outline" width={16} />
                                    <span className="hidden sm:inline">{t('Remove')}</span>
                                </button>
                            )}
                        </div>
                        {itemRows.map((row) => (
                            <div key={row[0].slot[0].key} className="grid w-full grid-cols-12 gap-2">
                                {row.map(({ slot, span }) => (
                                    <div key={slot[0].key} className={`min-w-0 ${COLUMN_SPAN_CLASS[span] ?? COLUMN_SPAN_CLASS[12]}`}>
                                        {control(item, index, slot[0].key, fields[slot[0].key])}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ))}
                {!readOnly && !follows && (
                    <button
                        type="button" aria-label={t('Add')} title={t('Add')}
                        className="flex items-center gap-1 self-start text-sm text-primary"
                        onClick={() => write([...items, blank()])}
                    >
                        <Icon icon="mdi:plus" width={16} />
                        <span className="hidden sm:inline">{t('Add')}</span>
                    </button>
                )}
            </div>
        );
    };

    const renderChecklist = (fd: FieldDef) => {
        const baseItems = resolveListItems(fd);
        const { states: rawStates, extra: extraItems } = parseListFieldState(formData[fd.key]);
        const items = [...baseItems, ...extraItems];
        const toggleField = fd.itemSchema?.toggleField;
        // A stored toggle state (from a prior interaction) always wins; otherwise a source item
        // can declare its own initial state via `toggleField`, defaulting to enabled.
        const states = items.map((item, idx) => {
            if (typeof rawStates[idx] === 'boolean') return rawStates[idx];
            return toggleField && typeof item[toggleField] === 'boolean' ? item[toggleField] : true;
        });
        const labelField = fd.itemSchema?.labelField ?? 'name';
        const requiredField = fd.itemSchema?.requiredField ?? 'required';
        const typeField = fd.itemSchema?.typeField ?? 'mediaType';
        const allowAdd = !!fd.itemSchema?.allowAdd;
        const addOptions = fd.itemSchema?.addOptions ?? [];

        const persist = (nextStates: boolean[], nextExtra: Record<string, any>[]) => {
            onFieldChange(fd.key, JSON.stringify({ states: nextStates, extra: nextExtra }));
        };

        const toggle = (idx: number, value: boolean) => {
            persist(states.map((s, i) => (i === idx ? value : s)), extraItems);
        };

        const updateExtraItem = (extraIdx: number, patch: Record<string, any>) => {
            persist(states, extraItems.map((item, i) => (i === extraIdx ? { ...item, ...patch } : item)));
        };

        const removeExtraItem = (extraIdx: number) => {
            persist(
                states.filter((_, i) => i !== baseItems.length + extraIdx),
                extraItems.filter((_, i) => i !== extraIdx),
            );
        };

        const addItem = (typeValue?: string) => {
            const newItem: Record<string, any> = { [labelField]: '', [requiredField]: false };
            if (typeValue) newItem[typeField] = typeValue;
            persist([...states, true], [...extraItems, newItem]);
            setAddingListKey(null);
        };

        return (
            <div className="flex flex-col gap-2">
                <div className="rounded-lg border border-default-200 max-h-[320px] overflow-y-auto">
                    {items.length === 0 && (
                        <p className="text-sm text-default-500 py-3 px-3.5">
                            {fd.itemSchema?.emptyStateLabel ?? t('No items configured')}
                        </p>
                    )}
                    {items.map((item, idx) => {
                        const isExtra = idx >= baseItems.length;
                        const extraIdx = idx - baseItems.length;
                        const label = item[labelField] ?? '';
                        const isReq = !!item[requiredField];
                        const icon = resolveItemIcon(item, fd.itemSchema);
                        return (
                            <div key={isExtra ? `extra-${extraIdx}` : `base-${idx}`} className="flex flex-nowrap items-center gap-3 px-3.5 py-3 border-b border-default-100 last:border-b-0">
                                {icon && <Icon icon={icon} className="text-default-400 shrink-0" width={18} />}
                                {isExtra && !readOnly ? (
                                    <Input
                                        size="sm"
                                        variant="bordered"
                                        className="min-w-0 flex-1"
                                        classNames={{ input: 'overflow-x-auto whitespace-nowrap' }}
                                        placeholder={fd.itemSchema?.itemPlaceholder ?? t('Item name')}
                                        value={label}
                                        onValueChange={(v) => updateExtraItem(extraIdx, { [labelField]: v })}
                                        aria-label={fd.itemSchema?.itemPlaceholder ?? t('Item name')}
                                    />
                                ) : (
                                    <span className="flex min-w-0 flex-1 items-center text-sm text-foreground">
                                        <span className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap">
                                            {label || <span className="italic text-default-400">{fd.itemSchema?.emptyItemLabel ?? t('Untitled item')}</span>}
                                        </span>
                                        {isReq && <span className="text-danger ml-0.5 shrink-0">*</span>}
                                    </span>
                                )}
                                <Switch
                                    size="sm"
                                    className="shrink-0"
                                    isSelected={states[idx] ?? true}
                                    isDisabled={readOnly || (isReq && !!fd.itemSchema?.lockRequired)}
                                    onValueChange={(v) => toggle(idx, v)}
                                    aria-label={String(label || `item-${idx}`)}
                                />
                                {isExtra && !readOnly && (
                                    <button
                                        type="button"
                                        className="text-default-400 hover:text-danger shrink-0"
                                        aria-label={fd.itemSchema?.removeLabel ?? t('Remove item')}
                                        onClick={() => removeExtraItem(extraIdx)}
                                    >
                                        <Icon icon={fd.itemSchema?.removeIcon ?? 'solar:close-circle-linear'} width={18} />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                {allowAdd && !readOnly && (
                    addingListKey === fd.key && addOptions.length > 0 ? (
                        <div className="flex items-center justify-between gap-3 rounded-lg bg-default-100 px-3.5 py-3">
                            <span className="text-sm text-foreground">
                                {fd.itemSchema?.addPrompt ?? t('What type of item do you need to add?')}
                            </span>
                            <div className="flex items-center gap-2 shrink-0">
                                {addOptions.map((opt) => (
                                    <CtyButton key={opt.value} size="sm" variant="bordered" onPress={() => addItem(opt.value)}>
                                        {opt.icon && <Icon icon={opt.icon} width={16} />}
                                        {opt.label}
                                    </CtyButton>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="flex items-center gap-2 text-primary text-sm font-medium self-start"
                            onClick={() => (addOptions.length > 0 ? setAddingListKey(fd.key) : addItem())}
                        >
                            <Icon icon={fd.itemSchema?.addIcon ?? 'solar:add-circle-linear'} width={18} />
                            {fd.itemSchema?.addLabel ?? t('Add an item')}
                        </button>
                    )
                )}
            </div>
        );
    };

    /** A tab's kind is explicit when given (`kind`, or `content.type`); otherwise it's inferred
     *  from its key/label so a JSON author doesn't have to remember to set it for an obviously
     *  named tab. */
    const resolveTabKind = (tabDef: TabDef): 'list' | 'message' => {
        if (tabDef.kind === 'list' || tabDef.kind === 'message') return tabDef.kind;
        if (tabDef.content?.type === 'list' || tabDef.content?.type === 'string') {
            return tabDef.content.type === 'list' ? 'list' : 'message';
        }
        return `${tabDef.key} ${tabDef.label}`.toLowerCase().includes('list') ? 'list' : 'message';
    };

    const renderTabsField = (fd: FieldDef) => {
        const tabDefs = fd.tabs ?? [];
        return (
            <div className="flex flex-col gap-2 w-full rounded-lg border border-default-200 p-3" key={fd.key}>
                <Tabs aria-label={fd.key} size="sm" variant="underlined">
                    {tabDefs.map((tabDef) => (
                        <Tab key={tabDef.key} title={tabDef.label}>
                            {resolveTabKind(tabDef) === 'list' ? (() => {
                                // A `list` tab is self-contained when it carries its own `content.source`/
                                // `itemSchema`; falls back to the parent `tabs` field's own source/itemSchema
                                // otherwise (older, single-list-per-tabs-field shape).
                                const listFieldDef: FieldDef = {
                                    ...fd,
                                    key: `${fd.key}__${tabDef.key}`,
                                    type: 'list',
                                    source: tabDef.content?.source ?? fd.source,
                                    itemSchema: tabDef.content?.itemSchema ?? fd.itemSchema,
                                };
                                return renderChecklist(listFieldDef);
                            })() : (() => {
                                const stateKey = tabDef.content?.field ?? `${fd.key}__${tabDef.key}`;
                                const val = formData[stateKey] ?? '';
                                return (
                                    <Textarea
                                        minRows={compact ? 3 : 4}
                                        size={compact ? 'sm' : 'md'}
                                        aria-label={tabDef.label}
                                        placeholder={`${t('Enter')} ${tabDef.label.toLowerCase()}`}
                                        value={val}
                                        onValueChange={readOnly ? undefined : (v) => onFieldChange(stateKey, v)}
                                        isReadOnly={readOnly}
                                    />
                                );
                            })()}
                        </Tab>
                    ))}
                </Tabs>
            </div>
        );
    };

    /** Renders an `iframe` field from the URL the owning form resolved for it. */
    const renderIframeField = (fd: FieldDef) => {
        const { url, loading, error } = iframeState[fd.key] ?? {};
        if (loading) {
            return <div className="flex min-h-40 items-center justify-center"><Spinner size="md" /></div>;
        }
        if (error) {
            return (
                <div className="rounded-medium border border-danger p-4 text-center text-sm text-danger">
                    {t('Failed to load template')}
                </div>
            );
        }
        if (!url) return null;

        const getIframeUrl = (url: string) => {
            const separator = url.includes('?') ? '&' : '?';
            return `${url}${separator}targetUrl=${
                new URL(globalThis.location.href).origin
            }`;
        };

        // A single instance of the frame is mounted for the whole form: going full screen only
        // restyles the surrounding form, so this iframe keeps the same DOM node and never
        // reloads its document.
        return (
            <div style={{ height: fd.iframe?.height ?? 480 }}>
                <HtmlTemplateFrame
                    code=""
                    src={getIframeUrl(url)}
                    data={fd.iframe?.readyPayload}
                    readyEvent={fd.iframe?.readyEvent}
                    onMessage={(payload) => {
                        const config = fd.iframe;
                        if (!config || (config.outputEvent && payload?.type !== config.outputEvent)) return;
                        console.info('Command iframe output:', payload);
                        const output = readByPath(payload, config.outputPath);
                        if (output === undefined) return;
                        // Form values are strings, so structured output is stored as json and sent
                        // back as a real object by `buildCommandPayloadFields`.
                        onFieldChange(
                            fieldValueKey(fd),
                            typeof output === 'string' ? output : JSON.stringify(output),
                        );
                    }}
                    className="h-full w-full border-0 bg-white"
                />
            </div>
        );
    };

    /** The checklist loaded for an `inspectionItems` field, in the same editor the Inspection
     *  Templates settings screen uses. Edits stay in this form; the template is not changed. */
    const renderInspectionItems = (fd: FieldDef) => {
        const items = parseInspectionItems(formData[fd.key]);
        const { loading, error } = inspectionItemsState[fd.key] ?? {};
        const isRequired = isRequiredField(fd, formData);
        let status: ReactNode;
        if (loading) {
            status = <div className="flex min-h-24 items-center justify-center"><Spinner size="sm" /></div>;
        } else if (error) {
            status = (
                <div className="flex flex-col items-center gap-2 px-4 py-4 text-center text-sm text-danger">
                    {t('Failed to load items')}
                    {onRetryInspectionItems && (
                        <CtyButton size="sm" variant="bordered" onPress={onRetryInspectionItems}>{t('Retry')}</CtyButton>
                    )}
                </div>
            );
        }
        const showEmptyError = showErrors && !readOnly && !status && isRequired && items.length === 0;
        return (
            <>
                <InspectionChecklistEditor
                    items={items}
                    onChange={(next) => onFieldChange(fd.key, serializeInspectionItems(next))}
                    title={(
                        <>
                            {fieldLabel(fd)}
                            {isRequired && <span className="text-danger ml-0.5">*</span>}
                        </>
                    )}
                    status={status}
                    readOnly={readOnly}
                    allowAdd={fd.itemSchema?.allowAdd !== false}
                    showErrors={showErrors && !readOnly}
                    emptyLabel={fd.itemSchema?.emptyStateLabel}
                    className="max-h-[360px] overflow-y-auto"
                />
                {showEmptyError && <span className="text-danger text-xs">{t('Please add at least one item')}</span>}
            </>
        );
    };

    const renderAttachment = (fd: FieldDef, binding?: { value: string; onChange: (value: string) => void; limitKey?: string }) => {
        const raw = binding ? binding.value : formData[fd.key];
        const write = (value: string) => (binding ? binding.onChange(value) : onFieldChange(fd.key, value));
        const files = readStoredAttachments(raw);
        const limits = uploadLimitsForField(steps, binding?.limitKey ?? fd.key);
        const label = fieldLabel(fd).toLowerCase();
        const isEmpty = showErrors && !readOnly && isRequiredField(fd, formData) && files.length === 0;
        const inputId = `command-attachment-${fd.key}`;

        const onPick = async (list: FileList | null) => {
            const picked = Array.from(list ?? []);
            if (!picked.length || readOnly) return;
            const allowed = limits.multiple ? picked : picked.slice(0, 1);
            const accepted: File[] = [];
            for (const file of allowed) {
                const extension = /\.([^.]+)$/.exec(file.name.trim().toLowerCase())?.[1] ?? '';
                if (limits.accept?.length && !limits.accept.includes(extension)) {
                    CtyShowToast(checkType.error, `${t('Only these file types can be uploaded')}: ${limits.accept.join(', ')}`);
                    continue;
                }
                if (limits.maxSizeMb && file.size > limits.maxSizeMb * 1024 * 1024) {
                    CtyShowToast(checkType.error, `${file.name} ${t('must be smaller than')} ${limits.maxSizeMb} MB`);
                    continue;
                }
                accepted.push(file);
            }
            if (!accepted.length) return;
            const pending = accepted.map((file) => toLocalAttachment(file, { uploading: true }));
            const alreadyShown = shownAttachments(uploadsByField[fd.key], files);
            setUploadsByField((prev) => ({
                ...prev,
                [fd.key]: limits.multiple ? [...alreadyShown, ...pending] : pending,
            }));
            setUploadingKey(fd.key);
            try {
                const presigned = await getPresignedUrl(
                    accepted.map((file) => ({ filename: file.name, source_type: 'action' })),
                ).unwrap() as { data?: unknown } | unknown[] | undefined;
                const rawEntries = Array.isArray(presigned)
                    ? presigned
                    : Array.isArray(presigned?.data) ? presigned.data : [presigned];
                const stored: StoredAttachment[] = [];
                const finished: LocalAttachment[] = [];
                for (let index = 0; index < accepted.length; index += 1) {
                    const file = accepted[index];
                    const entry = rawEntries[index] as { presigned_url?: string; s3_key?: string; source_id?: string; content_type?: string } | undefined;
                    if (!entry?.presigned_url || !entry.s3_key) {
                        CtyShowToast(checkType.error, t('Upload failed'));
                        continue;
                    }
                    await uploadFileToS3({ file, presignedUrl: entry.presigned_url, CtyShowToast: () => undefined }).unwrap();
                    commandAttachmentFiles.set(entry.s3_key, file);
                    stored.push({
                        filename: file.name,
                        contentType: entry.content_type || file.type || 'application/octet-stream',
                        s3_key: entry.s3_key,
                        source_id: entry.source_id,
                        size: file.size,
                    });
                    finished.push(toLocalAttachment(file, { uploading: false, source_id: entry.source_id, s3_key: entry.s3_key }));
                }
                setUploadsByField((prev) => {
                    const current = prev[fd.key] ?? [];
                    const withoutPending = current.filter((item) => !pending.includes(item));
                    return { ...prev, [fd.key]: limits.multiple ? [...withoutPending, ...finished] : finished };
                });
                if (!stored.length) return;
                const next = limits.multiple ? [...files, ...stored] : stored.slice(0, 1);
                write(attachmentValue(next, limits.multiple));
            } catch (error) {
                setUploadsByField((prev) => ({
                    ...prev,
                    [fd.key]: (prev[fd.key] ?? []).filter((item) => !pending.includes(item)),
                }));
                CtyShowToast(checkType.error, (error as Error)?.message || t('Upload failed'));
            } finally {
                setUploadingKey(null);
            }
        };

        const previewFiles = shownAttachments(uploadsByField[fd.key], files);

        return (
            <div className="flex min-w-0 flex-col gap-1" key={fd.key}>
                {renderLabel(fd)}
                {previewFiles.length > 0 ? (
                    <AttachmentPreviewSection
                        enableImage
                        scroll
                        hideRemove={readOnly}
                        uploadedFiles={previewFiles}
                        setUploadedFiles={readOnly ? undefined : (update) => {
                            const next = typeof update === 'function' ? update(previewFiles) : update;
                            const kept = new Set(next.map((item) => (item as LocalAttachment).s3_key).filter((key): key is string => Boolean(key)));
                            setUploadsByField((prev) => ({ ...prev, [fd.key]: next as LocalAttachment[] }));
                            write(attachmentValue(files.filter((item) => kept.has(item.s3_key)), limits.multiple));
                        }}
                    />
                ) : files.length > 0 && (
                    <div className="flex min-w-0 max-w-full flex-nowrap gap-2 overflow-x-auto">
                        {files.map((file) => (
                            <span key={file.s3_key} className="shrink-0 text-sm text-foreground">{file.filename}</span>
                        ))}
                    </div>
                )}
                {!readOnly && (
                    <>
                        <input
                            id={inputId}
                            type="file"
                            className="hidden"
                            accept={limits.accept?.map((ext) => `.${ext}`).join(',')}
                            multiple={limits.multiple}
                            disabled={uploadingKey === fd.key}
                            onChange={(event) => {
                                void onPick(event.target.files);
                                event.target.value = '';
                            }}
                        />
                        <CtyButton
                            size="sm"
                            variant="flat"
                            className="w-fit"
                            isDisabled={uploadingKey === fd.key}
                            onPress={() => document.getElementById(inputId)?.click()}
                        >
                            {uploadingKey === fd.key ? t('Uploading...') : t('Upload')}
                        </CtyButton>
                    </>
                )}
                {isEmpty && <span className="text-xs text-danger">{`${t('Please upload')} ${label}`}</span>}
            </div>
        );
    };

    const renderField = (fd: FieldDef) => {
        if (fd.type === 'dropdown') return renderDropdown(fd);
        if (fd.type === 'date') return renderDateInput(fd);
        if (fd.type === 'radio') return renderRadio(fd);
        if (fd.type === 'attachment' || uploadSteps(steps).some((step) => uploadStepNamesField(step, fd.key))) return renderAttachment(fd);
        if (fd.type === 'string' || fd.type === 'number') return renderTextInput(fd);
        return null;
    };

    /** Renders one layout slot: the first of its mutually exclusive fields that is currently
     *  visible, or a placeholder that holds the slot open so the row doesn't reflow. */
    const renderSlot = (slot: FieldDef[]) => {
        const fd = slot.find((f) => isVisibleField(f, formData));
        if (!fd) return <div key={slot[0].key} aria-hidden="true" />;
        if (isPanelField(fd)) {
            return (
                <div key={fd.key} className="flex flex-col gap-1 w-full">
                    {fd.type === 'list' && renderLabel(fd)}
                    {fd.type === 'list' && renderChecklist(fd)}
                    {fd.type === 'repeatable' && renderLabel(fd)}
                    {fd.type === 'repeatable' && renderRepeatableList(fd)}
                    {fd.type === 'tabs' && renderTabsField(fd)}
                    {fd.type === 'iframe' && renderIframeField(fd)}
                    {fd.type === 'inspectionItems' && renderInspectionItems(fd)}
                </div>
            );
        }
        return renderField(fd);
    };

    // `hidden` fields are left out of the layout. useCommandPrefill still reads their
    // `inputelementref` into formData, and that value is sent with the command payload.
    // The live iframe is an input surface, not sent-message content. Its captured output remains
    // in formData/payload, but the external template is not fetched again for read-only messages.
    const layoutFieldDefs = fieldDefs.filter(
        (fd) => fd.type !== 'hidden' && !(readOnly && fd.type === 'iframe'),
    );
    const slots = buildFieldSlots(layoutFieldDefs);
    // A slot with nothing to show is dropped, so a hidden field (e.g. Phone Number while Send Via
    // is Email) doesn't leave an empty column; the fields after it move up into its place.
    const laidOut = slots.filter((slot) => slot.some((fd) => isVisibleField(fd, formData)));
    const fieldRows = packFieldRows(laidOut, formData, layoutTier);

    return (
        <div ref={formLayoutRef} className="flex w-full flex-col gap-2">
            {fieldRows.map((row) => (
                <div key={row[0].slot[0].key} className="grid w-full grid-cols-12 gap-2">
                    {row.map(({ slot, span }) => (
                        <div key={slot[0].key} className={`min-w-0 ${COLUMN_SPAN_CLASS[span] ?? COLUMN_SPAN_CLASS[12]}`}>
                            {renderSlot(slot)}
                        </div>
                    ))}
                </div>
            ))}
            {(hasCustomSelected || hasOptionMessage) && (() => {
                const msgEmpty = showErrors && !readOnly && hasCustomSelected && !customMessage?.trim();
                const isMsgReadOnly = readOnly || !hasCustomSelected;
                return (
                    <div className="flex w-full flex-col gap-1">
                        <label htmlFor="command_custom_message" className="text-xs font-medium text-[#414651] font-[Poppins]">
                            {t('Message')}
                            {hasCustomSelected && <span className="text-danger ml-0.5">*</span>}
                        </label>
                        <Textarea
                            id="command_custom_message"
                            minRows={compact ? 2 : 3}
                            maxRows={compact ? 4 : 6}
                            size={compact ? 'sm' : 'md'}
                            aria-label={t('Message')}
                            placeholder={t('Enter your message')}
                            value={customMessage}
                            onValueChange={hasCustomSelected ? onCustomMessageChange : undefined}
                            isReadOnly={isMsgReadOnly}
                            isInvalid={msgEmpty}
                            errorMessage={msgEmpty ? t('Please Enter Message') : undefined}
                            classNames={{ input: 'overflow-y-auto' }}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onEnterPress?.(); } }}
                        />
                    </div>
                );
            })()}
        </div>
    );
}


/** Shared header bar with form icon, counter, collapse toggle, expand button, and optional send button. */
function CommandFormHeader({
    filledCount,
    totalCount,
    isCollapsed,
    isFullScreen = false,
    onToggleCollapse,
    onExpand,
    onReset,
    onSend,
    isSendDisabled,
}: Readonly<{
    filledCount: number;
    totalCount: number;
    isCollapsed: boolean;
    isFullScreen?: boolean;
    onToggleCollapse: () => void;
    onExpand: () => void;
    onReset?: () => void;
    onSend?: () => void;
    isSendDisabled?: boolean;
}>) {
    const { t } = useTranslation();
    return (
        <div className="flex w-full shrink-0 items-center justify-between gap-2 px-3 select-none">
            <button
                type="button"
                className="flex min-w-0 flex-1 items-center gap-2 border-0 bg-transparent px-0 pt-2 pb-1 text-left font-inherit cursor-pointer"
                onClick={onToggleCollapse}
                aria-label={isCollapsed ? t('Expand form fields') : t('Collapse form fields')}
            >
                <Icon icon="mdi:document" width={16} height={16} className="text-primary shrink-0" />
                <span className="text-xs font-semibold text-[#414651] truncate">{t('Form')}</span>
                {totalCount > 0 && (
                    <span className="text-[10px] text-default-500 shrink-0">
                        {filledCount}/{totalCount}
                    </span>
                )}
                <Icon
                    icon={isCollapsed ? 'mdi:chevron-down' : 'mdi:chevron-up'}
                    width={14}
                    height={14}
                    className="text-default-500 shrink-0 transition-transform duration-200"
                />
            </button>
            <span className="flex items-center gap-1 shrink-0 pt-2 pb-1">
                {onReset && (
                    <Tooltip content={t('Reset')} placement="top">
                        <CtyButton isIconOnly size="sm" variant="light" aria-label={t('Reset form')}
                            onPress={() => { onReset(); }}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                            <Icon icon="mdi:refresh" width={16} height={16} className="text-default-600" />
                        </CtyButton>
                    </Tooltip>
                )}
                <Tooltip content={isFullScreen ? t('Exit full screen') : t('Expand')} placement="top">
                    <CtyButton isIconOnly size="sm" variant="light"
                        title={isFullScreen ? t('Exit full screen') : t('Expand form')}
                        onPress={() => { onExpand(); }}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                        <Icon
                            icon={isFullScreen ? 'mdi:arrow-collapse' : 'mdi:arrow-expand'}
                            width={16}
                            height={16}
                            className="text-default-600"
                        />
                    </CtyButton>
                </Tooltip>
                {onSend && (
                    <Tooltip content={t('Send')} placement="top">
                        <CtyButton
                            isIconOnly
                            radius="lg"
                            size="sm"
                            variant="light"
                            isDisabled={isSendDisabled}
                            onPress={onSend}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                            <Icon className="[&>path]:stroke-[2px]" icon="icon-park-outline:send-one" width={20} color="DD3399" />
                        </CtyButton>
                    </Tooltip>
                )}
            </span>
        </div>
    );
}

const FORM_SCROLL_CLASS = 'min-h-0 overflow-y-auto px-3 pb-2';
/** Full-screen body: fills the overlay instead of the short inline strip. */
const FORM_FULL_SCREEN_SCROLL_CLASS = 'flex-1 overflow-y-auto px-4 pb-4';
/**
 * Backdrop that hosts the form when it goes full screen. `contents` keeps this element out of
 * the layout the rest of the time, so the form can expand and shrink without React inserting or
 * removing a wrapper — which would remount the fields, and reload any embedded template iframe.
 */
const FORM_FULL_SCREEN_BACKDROP_CLASS = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const noop = () => undefined;

const CommandFormGroupContext = createContext<{
    activeId: string | null;
    activate: (id: string | null) => void;
} | null>(null);

/** One expanded command form at a time across the prompt and every sent question. */
export function CommandFormGroup({ children }: Readonly<{ children: ReactNode }>) {
    const [activeId, setActiveId] = useState<string | null>('prompt');
    const value = useMemo(() => ({ activeId, activate: setActiveId }), [activeId]);
    return <CommandFormGroupContext.Provider value={value}>{children}</CommandFormGroupContext.Provider>;
}

function useFullScreenEscape(fullScreen: boolean, close: () => void): void {
    useEffect(() => {
        if (!fullScreen) return undefined;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') close();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [close, fullScreen]);
}

function useCommandFormDefaults(
    fieldDefs: FieldDef[],
    formData: Record<string, string>,
    readOnly: boolean,
    onChange?: (data: Record<string, string>) => void,
): void {
    useEffect(() => {
        if (readOnly) return;
        const missing = fieldDefs
            .filter((fd) => fd.defaultValue !== undefined && !formData[fd.key]?.trim())
            .map((fd) => [fd.key, fd.defaultValue]);
        if (missing.length > 0) onChange?.({ ...formData, ...Object.fromEntries(missing) });
    }, [fieldDefs, formData, readOnly, onChange]);
}

function commandFormProgress(
    fieldDefs: FieldDef[],
    formData: Record<string, string>,
    customMessage: string,
): { filled: number; total: number } {
    const visible = fieldDefs.filter((fd) => isVisibleField(fd, formData));
    const required = visible.filter((fd) => fd.required !== false);
    const hasCustom = visible.some(
        (fd) => fd.type === 'dropdown' && formData[fd.key] === CUSTOM_DROPDOWN_KEY,
    );
    const filled = required.filter((fd) => isFieldFilled(fd, formData)).length;
    return {
        filled: filled + Number(hasCustom && Boolean(customMessage?.trim())),
        total: required.length + Number(hasCustom),
    };
}

/** Applies the keys `next` changed relative to `base` (set or removed) on top of `latest`. */
function rebaseFormData(
    next: Record<string, string>,
    base: Record<string, string>,
    latest: Record<string, string>,
): Record<string, string> {
    const rebased = { ...latest };
    for (const key of new Set([...Object.keys(base), ...Object.keys(next)])) {
        if (next[key] === base[key]) continue;
        if (key in next) rebased[key] = next[key];
        else delete rebased[key];
    }
    return rebased;
}

/**
 * Each writer in the form (field edits, iframe output, defaults, dropdown auto-select and the
 * prefills) hands the host a whole form built from the data it last rendered with, and the host
 * stores it as given. When two writes land before the host re-renders — e.g. a `$sources` prefill
 * finishing just as the iframe posts its checklist — the later one would drop the earlier one's
 * values. Each write is therefore rebased onto whatever was last handed over and not yet rendered.
 */
function useRebasedFormDataChange(
    formData: Record<string, string>,
    onFormDataChange: ((data: Record<string, string>) => void) | undefined,
): (next: Record<string, string>) => void {
    const renderedRef = useRef(formData);
    const pendingRef = useRef<Record<string, string> | null>(null);
    if (renderedRef.current !== formData) {
        // The host re-rendered with what it was handed, so nothing is pending any more.
        renderedRef.current = formData;
        pendingRef.current = null;
    }
    const onChangeRef = useRef(onFormDataChange);
    onChangeRef.current = onFormDataChange;

    return useCallback((next: Record<string, string>) => {
        const pending = pendingRef.current;
        const merged = pending ? rebaseFormData(next, renderedRef.current, pending) : next;
        pendingRef.current = merged;
        onChangeRef.current?.(merged);
    }, []);
}

interface CollapsibleCommandFormProps {
    commandJson: Record<string, any>;
    formData: Record<string, string>;
    customMessage: string;
    aiworkerId?: string;
    onFormDataChange?: (data: Record<string, string>) => void;
    onCustomMessageChange?: (msg: string) => void;
    readOnly?: boolean;
    onSend?: () => void;
    isDisabled?: boolean;
    containerClassName: string;
    formId?: string;
    /** forEach steps. A step with dependsOnStepId keeps its list the same length as that step's list. */
    steps?: ReadonlyArray<WebAgentStep>;
    /** Cap the field list so a composer that is not a full-height panel can still scroll. */
    fillAvailableHeight?: boolean;
    /** Fields only, always open: the host supplies its own header and send button (e.g. a dialog). */
    bare?: boolean;
    /** Show the required-field errors, e.g. after the host's send button was pressed. */
    showErrors?: boolean;
}

/** Resizes each forEach child list to its parent's length (at least one item). Returns `formData`
 *  itself when nothing changed, so callers can compare by reference. */
function resizeFollowingRepeatables(
    followsMap: Record<string, string>,
    formData: Record<string, string>,
    fieldDefs: FieldDef[],
): Record<string, string> {
    let next = formData;
    for (const [childKey, parentKey] of Object.entries(followsMap)) {
        const count = Math.max(parseRepeatableItems(formData[parentKey]).length, 1);
        const child = parseRepeatableItems(formData[childKey]);
        if (child.length === count) continue;
        const fields = fieldDefs.find((fd) => fd.key === childKey)?.fields ?? {};
        const blank = () => Object.fromEntries(Object.keys(fields).map((key) => [key, '']));
        const resized = Array.from({ length: count }, (_, i) => ({ ...blank(), ...(child[i] ?? {}) }));
        next = { ...next, [childKey]: JSON.stringify(resized) };
    }
    return next;
}

function commandFormClasses(containerClassName: string, fullScreen: boolean, fillAvailableHeight: boolean) {
    if (fullScreen) {
        return {
            formClass: `${containerClassName} flex min-h-0 flex-col relative max-w-[1100px] max-h-[92vh] overflow-hidden shadow-large`,
            scrollClass: FORM_FULL_SCREEN_SCROLL_CLASS,
        };
    }
    const heightCap = fillAvailableHeight ? ' max-h-[min(50vh,28rem)]' : '';
    return {
        formClass: `${containerClassName} flex min-h-0 flex-col`,
        scrollClass: `${FORM_SCROLL_CLASS}${heightCap}`,
    };
}

/** Steps already passed in win. Otherwise load the active web-agent config when the form has an attachment field. */
function useAttachmentSteps(
    aiworkerId: string | undefined,
    commandJson: Record<string, any> | undefined,
    steps: ReadonlyArray<WebAgentStep> | undefined,
): ReadonlyArray<WebAgentStep> | undefined {
    const passedHasUpload = uploadSteps(steps).length > 0;
    const needsFetch = !passedHasUpload && parseFieldDefs(commandJson).some((fd) => fd.type === 'attachment');
    const [fetched, setFetched] = useState<WebAgentStep[] | undefined>(undefined);
    const [queryConfiguration] = useLazyGetAiWorkerConfigurationsQuery();
    useEffect(() => {
        if (!needsFetch || !aiworkerId) return undefined;
        let cancelled = false;
        queryConfiguration(buildActiveWebAgentConfigQuery(aiworkerId) as never).unwrap().then((response) => {
            if (cancelled) return;
            const config = (response as { configurations?: Array<{ config?: unknown }> })?.configurations?.[0]?.config;
            const loaded = parseInstructionsGroups(config).steps;
            setFetched(Array.isArray(loaded) ? loaded as WebAgentStep[] : []);
        }).catch(() => { if (!cancelled) setFetched([]); });
        return () => { cancelled = true; };
    }, [aiworkerId, needsFetch, queryConfiguration]);
    return passedHasUpload ? steps : (fetched ?? steps);
}

/**
 * Shared collapsible form surface (header + scrollable fields + full-screen mode) used by both
 * the editable prompt-input (`CommandFormPromptInput`) and the read-only sent-message view
 * (`CommandFormReadOnly`). Dropdown options are only fetched when editable.
 */
function CollapsibleCommandForm({
    commandJson,
    formData: rawFormData,
    customMessage,
    aiworkerId,
    onFormDataChange,
    onCustomMessageChange,
    readOnly = false,
    onSend,
    isDisabled,
    containerClassName,
    formId,
    steps,
    fillAvailableHeight = false,
    bare = false,
    showErrors: showErrorsProp = false,
}: Readonly<CollapsibleCommandFormProps>) {
    const { t } = useTranslation();
    const group = useContext(CommandFormGroupContext);
    const [fullScreen, setFullScreen] = useState(false);
    // Sent questions start collapsed. The prompt starts open unless the group says otherwise.
    const [collapsed, setCollapsed] = useState(readOnly);
    const isCollapsed = group && formId ? group.activeId !== formId : collapsed;
    const [sendAttempted, setShowErrors] = useState(false);
    const showErrors = sendAttempted || showErrorsProp;
    const [attachmentBusy, setAttachmentBusy] = useState(false);
    // Normalized once here so every field, counter, and iframe signature below can treat values
    // as text, whatever the host or an embedded template handed over.
    const formData = useMemo(() => normalizeCommandFormData(rawFormData), [rawFormData]);
    const emitFormData = useRebasedFormDataChange(formData, onFormDataChange);
    const fieldDefs = useMemo(() => parseFieldDefs(commandJson), [commandJson]);
    const attachmentSteps = useAttachmentSteps(aiworkerId, commandJson, steps);
    const followsMap = useMemo(() => repeatableFollows(attachmentSteps), [attachmentSteps]);
    const { dropdownOptions, dropdownLoading, dropdownRecords } = useCommandFormDropdowns(aiworkerId, commandJson, !readOnly);
    // Owned here rather than by the fields, so collapsing the form doesn't discard the resolved URL.
    const iframeState = useCommandIframeSources(commandJson, formData, !readOnly);
    const inspectionItems = useCommandInspectionItemsSources(fieldDefs, formData, emitFormData, !readOnly);

    useCommandPrefill(commandJson, formData, emitFormData, {
        aiworkerId,
        enabled: !readOnly,
        dropdownOptions,
    });
    const prefillLoading = useCommandSourcePrefill(commandJson, fieldDefs, formData, emitFormData, !readOnly);

    // An attachment upload finishes after other fields may have changed. Read the form at
    // write time so that completion does not put back the values from when the file was picked.
    const latestRef = useRef({ formData, dropdownRecords, dropdownLoading });
    latestRef.current = { formData, dropdownRecords, dropdownLoading };
    const handleFieldChange = useCallback(
        (key: string, value: string) => {
            const latest = latestRef.current;
            emitFormData(clearStaleCommandSelections(
                fieldDefs,
                { ...latest.formData, [key]: value },
                latest.dropdownRecords,
                latest.dropdownLoading,
            ));
        },
        [fieldDefs, emitFormData],
    );

    useEffect(() => {
        if (readOnly) return;
        const cleared = clearStaleCommandSelections(fieldDefs, formData, dropdownRecords, dropdownLoading);
        const next = selectLoneDropdownOptions(fieldDefs, cleared, dropdownOptions, dropdownRecords, dropdownLoading);
        if (next !== formData) emitFormData(next);
    }, [dropdownLoading, dropdownOptions, dropdownRecords, fieldDefs, formData, emitFormData, readOnly]);

    useEffect(() => {
        if (readOnly) return;
        const next = resizeFollowingRepeatables(followsMap, formData, fieldDefs);
        if (next !== formData) emitFormData(next);
    }, [followsMap, formData, fieldDefs, readOnly, emitFormData]);

    const handleReset = useCallback(() => {
        onFormDataChange?.({});
        onCustomMessageChange?.('');
        setShowErrors(false);
    }, [onFormDataChange, onCustomMessageChange]);

    const setExpanded = useCallback((open: boolean) => {
        if (group && formId) group.activate(open ? formId : null);
        else setCollapsed(!open);
    }, [formId, group]);

    const hasRequiredFields = fieldDefs.some((fd) => isRequiredField(fd, formData));
    // An empty command form has no required fields, so its send button stays on the host's
    // existing rule. A form that does have required fields stays disabled until they are filled.
    const missingRequired = hasRequiredFields && !validateCommandForm(commandJson, formData, customMessage).valid;
    const handleSendAttempt = useCallback(() => {
        setShowErrors(true);
        if (missingRequired || attachmentBusy) return;
        // Only drop out of full screen once the form can actually be sent — a rejected attempt
        // has to keep its errors on the expanded surface the user is looking at.
        if (validateCommandForm(commandJson, formData, customMessage).valid) {
            setFullScreen(false);
            setExpanded(false);
        }
        onSend?.();
    }, [attachmentBusy, commandJson, customMessage, formData, missingRequired, onSend, setExpanded]);

    const closeFullScreen = useCallback(() => setFullScreen(false), []);
    useFullScreenEscape(fullScreen, closeFullScreen);
    useCommandFormDefaults(fieldDefs, formData, readOnly, emitFormData);

    const progress = commandFormProgress(fieldDefs, formData, customMessage);
    const editableFieldChange = readOnly ? noop : handleFieldChange;
    const editableMessageChange = readOnly ? noop : (onCustomMessageChange ?? noop);
    const expanded = fullScreen || !isCollapsed;
    const backdropClass = fullScreen ? FORM_FULL_SCREEN_BACKDROP_CLASS : 'contents';
    const { formClass, scrollClass } = commandFormClasses(containerClassName, fullScreen, fillAvailableHeight);
    const fields = (
        <CommandFormFields
            commandJson={commandJson}
            formData={formData}
            customMessage={customMessage}
            onFieldChange={editableFieldChange}
            onCustomMessageChange={editableMessageChange}
            dropdownOptions={dropdownOptions}
            dropdownLoading={dropdownLoading}
            dropdownRecords={dropdownRecords}
            iframeState={iframeState}
            inspectionItemsState={inspectionItems.state}
            onRetryInspectionItems={inspectionItems.retry}
            prefillLoading={prefillLoading}
            readOnly={readOnly}
            compact={!fullScreen}
            showErrors={showErrors}
            onEnterPress={readOnly || bare ? undefined : handleSendAttempt}
            repeatableFollows={followsMap}
            steps={attachmentSteps}
            onUploadingChange={readOnly ? undefined : setAttachmentBusy}
        />
    );

    if (bare) {
        return <div className={containerClassName}>{fields}</div>;
    }

    // Full screen restyles this one form rather than opening a copy of it, so every field keeps
    // its DOM node and an embedded template iframe survives expanding and shrinking untouched.
    return (
        <div className={backdropClass}>
            {/* Clicking outside the full-screen form closes it; Escape does the same from the keyboard. */}
            {fullScreen && (
                <button
                    type="button"
                    aria-label={t('Exit full screen')}
                    tabIndex={-1}
                    className="absolute inset-0 cursor-default"
                    onClick={closeFullScreen}
                />
            )}
            <div className={formClass}>
                <CommandFormHeader
                    filledCount={progress.filled}
                    totalCount={progress.total}
                    isCollapsed={fullScreen ? false : isCollapsed}
                    isFullScreen={fullScreen}
                    onToggleCollapse={fullScreen ? noop : () => setExpanded(isCollapsed)}
                    onExpand={() => {
                        setExpanded(true);
                        setFullScreen((current) => !current);
                    }}
                    onReset={readOnly ? undefined : handleReset}
                    onSend={readOnly ? undefined : handleSendAttempt}
                    isSendDisabled={Boolean(isDisabled) || missingRequired || attachmentBusy}
                />
                {expanded && <div className={scrollClass}>{fields}</div>}
            </div>
        </div>
    );
}

export interface CommandFormPromptInputProps {
    commandJson: Record<string, any>;
    formData: Record<string, string>;
    customMessage: string;
    onFormDataChange: (data: Record<string, string>) => void;
    onCustomMessageChange: (msg: string) => void;
    onSend: () => void;
    aiworkerId?: string;
    isDisabled?: boolean;
    /** forEach steps, so a list that depends on another stays the same length. */
    steps?: ReadonlyArray<WebAgentStep>;
    /** Fill a height-bounded parent so the field list scrolls and the header stays visible. */
    fillAvailableHeight?: boolean;
}

/**
 * Full-replacement prompt input for command agents. Renders the form fields directly
 * with only a Send button and an Expand-to-modal button — no textarea, no other controls.
 */
export function CommandFormPromptInput({
    commandJson,
    formData,
    customMessage,
    onFormDataChange,
    onCustomMessageChange,
    onSend,
    aiworkerId,
    isDisabled = false,
    steps,
    fillAvailableHeight = false,
}: Readonly<CommandFormPromptInputProps>) {
    return (
        <CollapsibleCommandForm
            commandJson={commandJson}
            formData={formData}
            customMessage={customMessage}
            aiworkerId={aiworkerId}
            onFormDataChange={onFormDataChange}
            onCustomMessageChange={onCustomMessageChange}
            onSend={onSend}
            isDisabled={isDisabled}
            formId="prompt"
            steps={steps}
            fillAvailableHeight={fillAvailableHeight}
            containerClassName={`flex w-full flex-col rounded-medium bg-content1 border${fillAvailableHeight ? ' min-h-0 max-h-full overflow-hidden' : ''}`}
        />
    );
}

export interface CommandFormPanelProps {
    commandJson: Record<string, any>;
    formData: Record<string, string>;
    customMessage: string;
    onFormDataChange: (data: Record<string, string>) => void;
    onCustomMessageChange: (msg: string) => void;
    aiworkerId?: string;
    /** Show the required-field errors; set it when the host's send button is pressed with gaps left. */
    showErrors?: boolean;
    className?: string;
}

/**
 * The command form's fields with no header, collapse or send button, for a host that frames the
 * form itself (e.g. a Create dialog with its own Send). Dropdown sources, prefill and `$sources`
 * work as in the prompt input; validate with `validateCommandForm` and build the payload with
 * `useCommandPayloadBuilder`.
 */
export function CommandFormPanel({
    commandJson,
    formData,
    customMessage,
    onFormDataChange,
    onCustomMessageChange,
    aiworkerId,
    showErrors = false,
    className = 'flex w-full flex-col',
}: Readonly<CommandFormPanelProps>) {
    return (
        <CollapsibleCommandForm
            commandJson={commandJson}
            formData={formData}
            customMessage={customMessage}
            aiworkerId={aiworkerId}
            onFormDataChange={onFormDataChange}
            onCustomMessageChange={onCustomMessageChange}
            bare
            showErrors={showErrors}
            containerClassName={className}
        />
    );
}

export interface CommandFormReadOnlyProps {
    commandJson: Record<string, any>;
    formData: Record<string, string>;
    customMessage: string;
    aiworkerId?: string;
    formId?: string;
}

/**
 * Read-only inline form view used inside sent chat messages. Shows fields with fixed
 * height, vertical scroll, and an expand button to open the full modal.
 */
export function CommandFormReadOnly({
    commandJson,
    formData,
    customMessage,
    aiworkerId,
    formId,
}: Readonly<CommandFormReadOnlyProps>) {
    return (
        <CollapsibleCommandForm
            commandJson={commandJson}
            formData={formData}
            customMessage={customMessage}
            aiworkerId={aiworkerId}
            formId={formId}
            readOnly
            containerClassName="w-full bg-default-50 border border-default-200 rounded-medium flex flex-col"
        />
    );
}